import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import StaffHeader from '../components/StaffHeader';
import { inZone } from '../utils/companyDate';
import { unsubscribedLine, selfUnsubscribedNote } from '../utils/emailUnsubscribe';
import { formatPhoneDisplay, formatPhoneInput, stripPhone } from '../utils/phoneFormat';
import { formatPhone } from '../utils/formatPhone';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { formatCurrency } from '../utils/formatCurrency';
import { hasStopPrice, stopTotal, formatStopTotal, NO_PRICE_LABEL, AWAITING_DEPTH_LABEL } from '../utils/stopPrice';
import EmailCustomersModal from '../components/EmailCustomersModal';
import MessageHistory from '../components/MessageHistory';
import BulkPortalInviteModal from '../components/BulkPortalInviteModal';
import DoNotServeBadge from '../components/DoNotServeBadge';
import SmsConsentBadge from '../components/SmsConsentBadge';
import SmsConsentField from '../components/SmsConsentField';
import SizeConfirmButton from '../components/SizeConfirmButton';
import IntakeSignupsNotice, { useIntakeSignups } from '../components/IntakeSignupsNotice';
import ServiceRequestsNotice, { useServiceRequestsNotice } from '../components/ServiceRequestsNotice';
import CustomerServiceRequests from '../components/CustomerServiceRequests';
import CustomerAddons, { CustomerAddonBadges } from '../components/CustomerAddons';
import { isUnreviewedSignup, showsNewSignupBadge, newSignupsFirst } from '../utils/intakeSignup';
import { sizeConfirmBody } from '../utils/sizeConfirm';
import { welcomeLabel, welcomeActionLabel, welcomeConfirmText, welcomeBody } from '../utils/welcome';
import {
  SEASON_FILTERS, seasonBadge, matchesSeasonFilter, seasonFilterPrintLabel, chooserDefaults, confirmBody, seasonSavedMessage,
} from '../utils/seasonConfirm';
import CustomerTexts from '../components/CustomerTexts';
import useSmsEnabled from '../hooks/useSmsEnabled';
import { DEFAULT_PAYMENT_METHODS, methodLabel, preferredMethodOptions, preferredMethodDefault } from '../utils/paymentMethods';

// The same sentence the intake form shows the customer, shown to the owner so
// both sides of the conversation are working from one definition of a size.
// Names no tier, so renaming or repricing a tier cannot make it false.
const SIZE_FRAMING = 'Count parking spaces on the driveway surface, counting both width and length, not how many cars the customer owns.';

// The definition block that sits under a tier select. Extracted once and used
// under BOTH selects, the customer form and the property modal, so the two
// cannot drift into saying different things about the same tier.
//
// The per-tier line is pricing_tiers.description, written by the owner in
// Settings. When it is NULL the line is simply omitted: there is no hardcoded
// fallback keyed on the tier name, because a company that renamed its tiers
// would then be shown a definition it never wrote.
function TierSizeDefinition({ tierId, tiers }) {
  const tier = tierId && tierId !== 'custom'
    ? tiers.find(t => String(t.id) === String(tierId))
    : null;
  const description = (tier && tier.description) || null;

  return (
    <div className="tier-size-definition">
      <p className="tier-size-framing">{SIZE_FRAMING}</p>
      {description && <p className="tier-size-meaning">{tier.name}: {description}</p>}
    </div>
  );
}

// The ONE place a tier turns into a pair of prices.
//
// This was written inline inside handleFormChange and again, slightly
// differently, inside handlePropertyFormChange. Two inline tier dropdowns now
// need the same answer as well, and four copies of "what does Large cost" is
// how the row and the modal would start disagreeing about the same tier.
//
// Returns null for Custom, for an empty value, and for a tier id that is not in
// the active list. Null means LEAVE THE PRICES ALONE - which is what Custom has
// always meant in the modal, and which the inline dropdown expresses by sending
// no price keys at all rather than by sending the old values back.
//
// Strings, two decimals, because the modal binds these straight into text
// inputs. The inline handler parses them back to numbers for the wire.
function tierPrices(tierId, tiers) {
  if (!tierId || tierId === 'custom') return null;
  const tier = tiers.find(t => String(t.id) === String(tierId));
  if (!tier) return null;
  return {
    priceSnow: parseFloat(tier.default_price).toFixed(2),
    saltPrice: parseFloat(tier.salt_price || 0).toFixed(2),
  };
}

// The inline tier dropdown, used on a customer row AND on a property row.
// One component, two call sites, because a property is a customers row and the
// only thing that differs between them is the size of the chip.
//
// `row` is whichever row the chip describes - a customer, or the first snow
// property standing in for a lawn-only customer. It is passed straight back to
// onChange, so the caller never has to work out which row it was looking at.
//
// Options are `tiers`, the same array the edit modal's select maps over, from
// the same /api/pricing-tiers?product=snow load. Same source, so the row and
// the modal cannot offer different sizes.
//
// Labelled by NAME ALONE, without the modal's " - $85.00" suffix. A select
// sizes itself to its WIDEST option, not its selected one, so carrying the
// price would widen this chip on every row by the length of the longest tier
// name plus a price - and the row already prints the price on the right.
//
// The retired-tier option is the one thing here the modal does not have. A row
// can hold a snow_tier_id whose tier was later deactivated: the server still
// resolves the name for the badge, but the tier is gone from the active list,
// so a select built from that list alone has no option matching its own value
// and browsers fall back to rendering the first option. The row would then
// read "Small" for a customer who is not on Small. The extra option is
// disabled, so it shows the truth without offering a retired size to anyone
// who is not already on it.
function TierQuickSelect({ row, tiers, className, disabled, onChange }) {
  const currentId = row.snow_tier_id ? String(row.snow_tier_id) : 'custom';
  const isRetired = currentId !== 'custom' && !tiers.some(t => String(t.id) === currentId);

  return (
    <select
      className={`${className} ${row.snow_tier_name ? '' : 'custom'}`}
      value={currentId}
      onChange={(e) => onChange(row, e.target.value)}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      aria-label="Pricing tier"
    >
      {isRetired && (
        <option value={currentId} disabled>{row.snow_tier_name || 'Retired tier'}</option>
      )}
      {tiers.map(tier => (
        <option key={tier.id} value={String(tier.id)}>{tier.name}</option>
      ))}
      <option value="custom">Custom</option>
    </select>
  );
}

// What the customer said on the intake form, for one address.
//
// A REQUEST and nothing else. It is read only, it carries no price, and it does
// not change the tier: the owner still chooses that with the select above it.
// Renders nothing when no request was recorded, which is every customer created
// before this feature and every customer the owner added by hand.
function RequestedSizeNote({ requestedName }) {
  if (!requestedName) return null;
  return (
    <p className="requested-size-note">Customer said: {requestedName}</p>
  );
}

function Customers() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, shouldHidePrices, isPro, isLapsed, timezone } = useAuth();
  // The Texts section on each card: owner and admin, Pro (or lapsed, read
  // only), and only while SMS_ENABLED is on. The server enforces all three.
  const smsEnabled = useSmsEnabled();
  const showTexts = smsEnabled && isAdmin() && (isPro() || isLapsed());

  // What the Dashboard's size banner asked for, read ONCE on arrival. A name
  // click carries the card to open; the banner body carries the filter.
  //
  // The view is decided here, before the first load, from the card's own state:
  // an archived customer is only in the "inactive only" list, while a held
  // signup is already merged into the default list, so it must NOT flip to
  // inactive. Getting this wrong lands the owner on a list without the card.
  const arrival = location.state || {};
  const arrivalFocusesArchived = Boolean(arrival.focusCustomerId)
    && arrival.focusIsActive === false
    && arrival.focusIntakeStatus !== 'waitlisted';

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(arrivalFocusesArchived);
  // A view, not a saved filter: every Do Not Serve customer, active or not.
  const [doNotServeOnly, setDoNotServeOnly] = useState(false);
  // Customers or properties still waiting on the owner's driveway size decision.
  const [sizeUnconfirmedOnly, setSizeUnconfirmedOnly] = useState(Boolean(arrival.sizeUnconfirmedOnly));
  // Intake signups the owner has not reviewed yet, as the notice lists them.
  const [newSignupsOnly, setNewSignupsOnly] = useState(false);
  // The season answer to show: all, confirmed, declined or pending (No reply).
  // The Dashboard Season tile's links arrive with one set.
  const [seasonFilter, setSeasonFilter] = useState(
    SEASON_FILTERS.some((f) => f.value === arrival.seasonFilter) ? arrival.seasonFilter : 'all'
  );
  // The inline Confirmed chooser: the customer it is open on and its choices.
  const [seasonChooser, setSeasonChooser] = useState(null);
  const [savingSeason, setSavingSeason] = useState(null);
  // The card the banner sent the owner to, until it has been scrolled to.
  // `expires` stops a card that never turns up from being jumped to later,
  // when the owner has moved on and changes a filter that happens to reveal it.
  const pendingFocus = useRef(arrival.focusCustomerId ? {
    customerId: arrival.focusCustomerId,
    propertyId: arrival.focusPropertyId || null,
    // The new-signups card's Review button opens the edit form as well.
    openEdit: arrival.focusOpenEdit === true,
    expires: Date.now() + 10000,
  } : null);
  const [highlightedId, setHighlightedId] = useState(null);
  // Bumped by the new-signups notice's Review when the card is already in the
  // loaded list, so the focus effect below runs without a reload.
  const [focusRequest, setFocusRequest] = useState(0);
  // The Dashboard's new-signups notice, shown here too. Owner and admin only.
  const {
    items: intakeSignups,
    reviewingId: intakeReviewingId,
    error: intakeSignupsError,
    load: loadIntakeSignups,
    markReviewed: markIntakeSignupReviewed,
  } = useIntakeSignups();
  // The Dashboard's "N new service requests" notice, shown here too.
  const {
    notice: serviceRequestsNotice,
    dismissing: dismissingServiceRequests,
    error: serviceRequestsNoticeError,
    load: loadServiceRequestsNotice,
    dismiss: dismissServiceRequests,
  } = useServiceRequestsNotice();
  // How many intake signups are currently held by the plan customer limit.
  const [heldCount, setHeldCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // True when `error` is a plan-limit refusal rather than a validation or
  // server fault, so the modal can add the upgrade link and nothing else does.
  const [errorIsLimit, setErrorIsLimit] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);
  const [printIncludeNotes, setPrintIncludeNotes] = useState(false);
  // What each customer owes, from GET /api/customers/balances, keyed by the
  // parent customer id: { owed, unpricedCount }. Absent means settled. Never
  // loaded for a role that cannot see prices (the server refuses them too).
  const [balances, setBalances] = useState({});
  const [owesOnly, setOwesOnly] = useState(false);
  // The statement view: the customer it is for, the period control, and the
  // server's statement. Dates are YYYY-MM-DD strings end to end; the printed
  // dates come from the server already formatted from date parts in the company's zone.
  const [statementCustomer, setStatementCustomer] = useState(null);
  const [statementPeriod, setStatementPeriod] = useState('season');
  const [statementStart, setStatementStart] = useState('');
  const [statementEnd, setStatementEnd] = useState('');
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementError, setStatementError] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showMessageHistory, setShowMessageHistory] = useState(false);
  const [showBulkInvite, setShowBulkInvite] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState({});
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [propertyParent, setPropertyParent] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [propertyFormData, setPropertyFormData] = useState({
    address: '',
    city: '',
    state: '',
    zip: '',
    // The tier is held as the pricing_tiers id (as a string, because that is
    // what a select value is), or the literal 'custom' for no tier. It is sent
    // as snowTierId and loaded back from the customer's snow_tier_id FK.
    snowTierId: 'custom',
    // DISPLAY ONLY. The size this property's owner asked for on the intake
    // form, as a name. It is never sent back on save: the request is the
    // customer's, the decision is the owner's, and the save carries the
    // decision.
    requestedSnowTierName: null,
    priceSnow: '50.00',
    saltPrice: '8.00',
    paymentMethodPreferred: '', // set when a form opens
    driveway: true,
    sidewalks: false,
    walkways: false,
    salt: false,
    saltPreference: 'never',
    serviceType: 'snow_removal',
    notes: '',
  });

  // State for quick property service type updates
  const [updatingPropertyServiceType, setUpdatingPropertyServiceType] = useState(null);

  // State for the inline tier dropdown. ONE piece of state for customer rows
  // and property rows both, unlike the service-type pair above, because a
  // property IS a customers row and the two id spaces are the same one. Two
  // states would only be two ways to say the same thing.
  const [updatingTier, setUpdatingTier] = useState(null);
  // The customer id whose welcome is being sent, or null.
  const [sendingWelcome, setSendingWelcome] = useState(null);

  // Payment methods state
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });

  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'info' }), 5000);
  };

  const [paymentMethods, setPaymentMethods] = useState(DEFAULT_PAYMENT_METHODS);
  // Whether Square can be a customer's preferred method: false when the
  // company has no active Square connection (GET /api/settings/preferred-method).
  // Until that answers, the form behaves as it always has.
  const [squareAvailable, setSquareAvailable] = useState(true);
  // A new customer's or property's method: the first method that is on,
  // Square only when the company can take it.
  const newCustomerMethod = () => preferredMethodDefault(paymentMethods, squareAvailable);
  const methodOptionsFor = (currentId) => preferredMethodOptions(paymentMethods, currentId, { squareAvailable });

  // Lead sources state
  const [leadSources, setLeadSources] = useState([
    { id: 'facebook', name: 'Facebook', enabled: true, isBuiltIn: true },
    { id: 'craigslist', name: 'Craigslist', enabled: true, isBuiltIn: true },
    { id: 'flyer', name: 'Flyer', enabled: true, isBuiltIn: true },
    { id: 'yard_sign', name: 'Yard Sign', enabled: true, isBuiltIn: true },
    { id: 'referral', name: 'Friend/Referral', enabled: true, isBuiltIn: true },
    { id: 'google', name: 'Google', enabled: true, isBuiltIn: true },
    { id: 'nextdoor', name: 'Nextdoor', enabled: true, isBuiltIn: true },
    { id: 'other', name: 'Other', enabled: true, isBuiltIn: true },
  ]);

  // Pricing tiers state
  const [pricingTiers, setPricingTiers] = useState([]);
  const [pricingTiersLoaded, setPricingTiersLoaded] = useState(false);
  // The Dashboard Getting started card's "Add customer" arrives with
  // { openAddCustomer: true } and opens the Add Customer form once the tiers
  // it defaults from have loaded.
  const pendingAddCustomer = useRef(Boolean(arrival.openAddCustomer));

  // Portal invite state
  const [portalStatus, setPortalStatus] = useState({});
  const [invitingPortal, setInvitingPortal] = useState(null);

  // Get only enabled lead sources
  const enabledLeadSources = leadSources.filter(s => s.enabled);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    snowTierId: 'custom',
    // DISPLAY ONLY, never sent on save. See the note on propertyFormData above.
    requestedSnowTierName: null,
    priceSnow: '50.00',
    saltPrice: '8.00',
    driveway: true,
    sidewalks: false,
    walkways: false,
    salt: false,
    paymentMethodPreferred: '', // set when a form opens
    snowfallThreshold: 'any',
    servicePreference: 'on_call',
    serviceType: 'snow_removal',
    referredById: '',
    billingSameAsService: true,
    billingName: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    notes: '',
    leadSource: '',
    discountAmount: '',
    discountType: '',
    discountReason: '',
    emailSubscribed: true,
    doNotServe: false,
    doNotServeReason: '',
  });

  // State for quick service type updates
  const [updatingServiceType, setUpdatingServiceType] = useState(null);

  const loadCustomers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (doNotServeOnly) {
        // No active filter at all. A flagged customer the owner also archived
        // is still flagged, and would be missing from this view if it loaded
        // only one side of is_active.
      } else if (showInactive) {
        // When checked, show ONLY inactive customers
        params.append('active', 'false');
      } else {
        // When unchecked, show active customers only
        params.append('active', 'true');
      }
      // Include children to get property data
      params.append('includeChildren', 'true');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/customers?${params.toString()}`
      );

      // Signups held by the plan limit are written inactive, so the default
      // "active only" view would hide them completely - and a held signup
      // nobody sees is no better than one we dropped. They are fetched
      // separately and shown at the top of the ordinary list, badged, until
      // the owner activates or deletes them. In the "inactive only" view they
      // are already in the response, so nothing is added and nothing doubles.
      let held = [];
      // The Do Not Serve view already loaded every row, held ones included,
      // so adding them again would double them.
      if (!showInactive && !doNotServeOnly) {
        try {
          const heldResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/customers?active=false&includeChildren=true`
          );
          held = (heldResponse.data || []).filter(
            (c) => c.intake_status === 'waitlisted' && c.parent_customer_id == null
          );
        } catch (heldErr) {
          // A failure here must not blank the customer list.
          console.error('Failed to load held signups:', heldErr);
        }
      }

      setHeldCount(held.length);
      setCustomers([...held, ...response.data]);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setLoading(false);
    }
  }, [showInactive, doNotServeOnly]);

  const loadPricingTiers = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/pricing-tiers?product=snow`);
      setPricingTiers(response.data);
    } catch (err) {
      console.error('Failed to load pricing tiers:', err);
    } finally {
      setPricingTiersLoaded(true);
    }
  }, []);

  const loadPaymentMethods = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/payment-methods`);
      if (response.data && response.data.length > 0) {
        setPaymentMethods(response.data);
      }
    } catch (err) {
      console.error('Failed to load payment methods:', err);
    }
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/preferred-method`);
      if (response.data && typeof response.data.squareAvailable === 'boolean') {
        setSquareAvailable(response.data.squareAvailable);
      }
    } catch (err) {
      console.error('Failed to load the payment method default:', err);
    }
  }, []);

  const loadLeadSources = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/lead-sources`);
      if (response.data && response.data.length > 0) {
        setLeadSources(response.data);
      }
    } catch (err) {
      console.error('Failed to load lead sources:', err);
    }
  }, []);

  // Loaded beside the list, not inside it, so the cards render as fast as
  // before and the balances fill in a moment later. A failure leaves the cards
  // without a balance rather than breaking the page.
  const hidePrices = user?.role === 'driver' || user?.role === 'crew_lead';
  const loadBalances = useCallback(async () => {
    if (hidePrices) return;
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers/balances`);
      setBalances(response.data || {});
    } catch (err) {
      console.error('Failed to load balances:', err);
    }
  }, [hidePrices]);

  const refreshList = useCallback(async () => {
    await Promise.all([loadCustomers(), loadBalances()]);
  }, [loadCustomers, loadBalances]);

  const { PullToRefreshIndicator } = usePullToRefresh(refreshList);

  // Helper to calculate discounted price
  const calculateDiscountedPrice = (price, discountAmount, discountType) => {
    if (!discountAmount || discountAmount <= 0) return price;
    if (discountType === 'percent') {
      return Math.max(0, price - (price * discountAmount / 100));
    }
    return Math.max(0, price - discountAmount);
  };

  useEffect(() => {
    loadCustomers();
    loadPricingTiers();
    loadPaymentMethods();
    loadLeadSources();
  }, [loadCustomers, loadPricingTiers, loadPaymentMethods, loadLeadSources]);

  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  // The notice follows the list: every save, activate and archive reloads the
  // customers, and a save of the edit form is what reviews a signup.
  useEffect(() => {
    if (loading || !isAdmin()) return;
    loadIntakeSignups();
    loadServiceRequestsNotice();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, loading, loadIntakeSignups, loadServiceRequestsNotice]);

  // Depth pricing (design 4.3): when the company prices snow by depth and
  // has an on-call premium, on-call customers show "On-call +20%". Owner and
  // admin only (GET /api/depth-pricing is); anyone else sees plain On-Call.
  const [onCallPremium, setOnCallPremium] = useState(null);
  useEffect(() => {
    if (!isAdmin()) return;
    axios.get(`${process.env.REACT_APP_API_URL}/api/depth-pricing`)
      .then(res => {
        const pct = parseFloat(res.data?.onCallPremiumPercent);
        setOnCallPremium(res.data?.enabled === true && pct > 0 ? pct : null);
      })
      .catch(() => setOnCallPremium(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // A NULL preference is on-call: that is the column's default.
  const onCallLabel = onCallPremium ? `On-call +${Number(onCallPremium.toFixed(2))}%` : 'On-Call';

  const balanceOf = (customer) => balances[String(customer.id)] || null;
  const owesMoney = (customer) => {
    const b = balanceOf(customer);
    return Boolean(b && (b.owed > 0 || b.unpricedCount > 0));
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Unreviewed signups sort to the top, so a new one is the first card seen.
  const filteredCustomers = newSignupsFirst(customers.filter((customer) => {
    // Properties inherit the flag from the parent, so the parent row decides.
    if (doNotServeOnly && customer.do_not_serve !== true) return false;
    // A customer with an unpriced stop counts: what they owe is unknown, not
    // zero, and the card says so.
    if (owesOnly && !hidePrices && !owesMoney(customer)) return false;
    // A property carries its own flag, so a customer whose only pending row is
    // a property is still shown: that card is where the owner confirms it.
    if (sizeUnconfirmedOnly
      && !customer.size_confirmation_pending
      && !(customer.properties || []).some((p) => p.size_confirmation_pending)) return false;
    if (newSignupsOnly && !isUnreviewedSignup(customer)) return false;
    if (!matchesSeasonFilter(customer, seasonFilter)) return false;
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      customer.first_name?.toLowerCase().includes(searchLower) ||
      customer.last_name?.toLowerCase().includes(searchLower) ||
      customer.address?.toLowerCase().includes(searchLower) ||
      customer.city?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower)
    );
  }));

  // The router state has been read into the initial values above; drop it so a
  // refresh or a Back to this page does not jump to the card again.
  useEffect(() => {
    if (location.state) navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  // Scroll to and flash the card the Dashboard banner named. Runs on every list
  // change rather than once, because when the view flipped to inactive the
  // first list to arrive may not hold the card yet. A property inside the
  // collapsed "Additional Properties" list is expanded first, and the scroll
  // waits a frame for it to render.
  useEffect(() => {
    const focus = pendingFocus.current;
    if (!focus || loading) return;
    if (Date.now() > focus.expires) {
      pendingFocus.current = null;
      return;
    }
    const parent = customers.find((c) => String(c.id) === String(focus.customerId));
    if (!parent) return;
    pendingFocus.current = null;

    if (focus.propertyId) {
      setExpandedCustomers((prev) => ({ ...prev, [parent.id]: true }));
    }
    const targetId = focus.propertyId ? `property-card-${focus.propertyId}` : null;
    window.requestAnimationFrame(() => {
      const el = (targetId && document.getElementById(targetId))
        || document.getElementById(`customer-card-${parent.id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedId(el.id);
        window.setTimeout(() => setHighlightedId(null), 2500);
      }
      if (focus.openEdit) openEditModal(parent);
    });
    // openEditModal is a plain function redeclared each render; the effect
    // must not rerun for that, only when the list arrives or Review asks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, loading, focusRequest]);

  // Review on the notice: scroll to and flash that customer's card, without
  // leaving the page. The filters that could hide the card are cleared, and
  // the view flips when the card lives in the other one (an archived signup is
  // only in "inactive only"; a held one is already merged into the default
  // list). A flip reloads the list, and the focus effect waits for it.
  const reviewIntakeSignup = (item) => {
    pendingFocus.current = {
      customerId: item.id,
      propertyId: null,
      openEdit: false,
      expires: Date.now() + 10000,
    };
    const wantsInactive = item.isActive === false && item.intakeStatus !== 'waitlisted';
    setSearch('');
    setOwesOnly(false);
    setSizeUnconfirmedOnly(false);
    setSeasonFilter('all');
    if (wantsInactive !== showInactive || doNotServeOnly) {
      setShowInactive(wantsInactive);
      setDoNotServeOnly(false);
    } else {
      setFocusRequest((n) => n + 1);
    }
  };

  // Mark reviewed on the notice. The card's badge goes with it, without a
  // reload, by stamping the parent and its properties as the server did.
  const handleMarkIntakeReviewed = async (item) => {
    const marked = await markIntakeSignupReviewed(item);
    if (!marked) return;
    const stamp = new Date().toISOString();
    setCustomers((prev) => prev.map((c) => (String(c.id) !== String(item.id) ? c : {
      ...c,
      intake_reviewed_at: c.intake_reviewed_at || stamp,
      properties: (c.properties || []).map((p) => ({ ...p, intake_reviewed_at: p.intake_reviewed_at || stamp })),
    })));
  };

  // The printed list is exactly what is on screen, minus held signups: they
  // are not customers until the owner activates them. Same test as
  // isHeldByPlanLimit, spelled out because that const is declared further down
  // and this runs during render before it exists.
  const printCustomers = filteredCustomers.filter(
    (c) => !(c.is_active === false && c.intake_status === 'waitlisted')
  );

  const openPrintView = () => {
    // Notes carry gate codes, so every print starts with them off.
    setPrintIncludeNotes(false);
    setShowPrintView(true);
  };

  const handlePrint = () => {
    window.print();
  };

  // Says on paper which of the page's filters produced this list.
  const getPrintFilterLabel = () => {
    const base = doNotServeOnly
      ? 'Do not serve only (active and inactive)'
      : showInactive
        ? 'Inactive customers only'
        : 'Active customers';
    const withOwes = owesOnly && !hidePrices ? `${base} · Owes money only` : base;
    const withSize = sizeUnconfirmedOnly ? `${withOwes} · Size unconfirmed only` : withOwes;
    const withNew = newSignupsOnly ? `${withSize} · New signups only` : withSize;
    const seasonLabel = seasonFilterPrintLabel(seasonFilter, customers.find((c) => c.season_key)?.season_key);
    const withSeason = seasonLabel ? `${withNew} · ${seasonLabel}` : withNew;
    return search ? `${withSeason} · Search: "${search}"` : withSeason;
  };

  const loadStatement = async (customerId, period, start, end) => {
    setStatementLoading(true);
    setStatementError('');
    try {
      const params = new URLSearchParams();
      if (period === 'all') params.append('period', 'all');
      if (period === 'custom') {
        if (start) params.append('start', start);
        if (end) params.append('end', end);
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/customers/${customerId}/statement?${params.toString()}`
      );
      setStatement(response.data);
    } catch (err) {
      setStatement(null);
      setStatementError(err.response?.data?.error || 'Failed to load statement');
    } finally {
      setStatementLoading(false);
    }
  };

  const openStatement = (customer) => {
    setStatementCustomer(customer);
    setStatementPeriod('season');
    setStatementStart('');
    setStatementEnd('');
    setStatement(null);
    loadStatement(customer.id, 'season');
  };

  const closeStatement = () => {
    setStatementCustomer(null);
    setStatement(null);
    setStatementError('');
  };

  const changeStatementPeriod = (period) => {
    setStatementPeriod(period);
    // Custom waits for dates and the Show button; the other two load at once.
    if (period !== 'custom') loadStatement(statementCustomer.id, period);
  };

  // Stops on this statement completed before the storm depth was entered
  // (design 5 row 6): listed as Awaiting depth, never charged or summed.
  const statementPendingDepthCount = (st) =>
    (st ? (st.pendingDepthCount || 0) + (st.openingPendingDepthCount || 0) : 0);
  const statementPendingDepthText = (st) => {
    const n = statementPendingDepthCount(st);
    return `This statement includes ${n} stop${n === 1 ? '' : 's'} waiting for a storm depth; their charges are not included.`;
  };

  // Money on the statement. A credit reads as one rather than as a bare minus.
  const statementMoney = (v) => (v < 0 ? `${formatCurrency(-v)} credit` : formatCurrency(v));

  const statementPeriodText = (s) => {
    if (!s) return '';
    const { label, startText, endText } = s.period;
    if (startText && endText) return `${label ? `${label}: ` : ''}${startText} to ${endText}`;
    if (startText) return `From ${startText}`;
    if (endText) return `Up to ${endText}`;
    return label || 'All services';
  };

  // Same price the card shows: the discounted figure when a discount applies,
  // the no-price label when none is set.
  const getPrintPrice = (row) => {
    if (!hasStopPrice(row)) return NO_PRICE_LABEL;
    if (row.discount_amount && row.discount_type) {
      return formatCurrency(calculateDiscountedPrice(
        stopTotal(row, row.salt),
        parseFloat(row.discount_amount),
        row.discount_type
      ));
    }
    return formatStopTotal(row, row.salt);
  };

  const getPrintServices = (row) => {
    const services = [];
    if (row.driveway) services.push('Driveway');
    if (row.sidewalks) services.push('Sidewalks');
    if (row.walkways) services.push('Walkways');
    return services.length > 0 ? services.join(', ') : 'None listed';
  };

  const getPrintPreference = (row) => {
    const preference = row.service_preference === 'seasonal' ? 'Seasonal' : 'On-Call';
    return row.snowfall_threshold && row.snowfall_threshold !== 'any'
      ? `${preference}, ${row.snowfall_threshold}"+ snow`
      : preference;
  };

  const getPaymentMethodName = (methodId) => methodLabel(paymentMethods, methodId);


  const openAddModal = () => {
    setEditingCustomer(null);
    // Use first pricing tier as default, fallback to 'custom'
    const defaultTier = pricingTiers.length > 0 ? pricingTiers[0] : null;
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      snowTierId: defaultTier ? String(defaultTier.id) : 'custom',
      // A customer the owner is adding by hand asked for nothing.
      requestedSnowTierName: null,
      priceSnow: defaultTier ? parseFloat(defaultTier.default_price).toFixed(2) : '50.00',
      saltPrice: defaultTier ? parseFloat(defaultTier.salt_price || 0).toFixed(2) : '8.00',
      driveway: true,
      sidewalks: false,
      walkways: false,
      salt: false,
      paymentMethodPreferred: newCustomerMethod(),
      snowfallThreshold: 'any',
      servicePreference: 'on_call',
      serviceType: 'snow_removal',
      referredById: '',
      billingSameAsService: true,
      billingName: '',
      billingAddress: '',
      billingCity: '',
      billingState: '',
      billingZip: '',
      notes: '',
      leadSource: '',
      discountAmount: '',
      discountType: '',
      discountReason: '',
      emailSubscribed: true,
      doNotServe: false,
      doNotServeReason: '',
    });
    setError('');
    setErrorIsLimit(false);
    setShowModal(true);
  };

  useEffect(() => {
    if (!pendingAddCustomer.current || !pricingTiersLoaded) return;
    pendingAddCustomer.current = false;
    openAddModal();
    // openAddModal is rebuilt every render; this runs once, when the tiers land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingTiersLoaded]);

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      firstName: customer.first_name || '',
      lastName: customer.last_name || '',
      email: customer.email || '',
      phone: formatPhoneDisplay(customer.phone) || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip: customer.zip || '',
      // Loaded from the FK, not from the legacy customers.tier text.
      snowTierId: customer.snow_tier_id ? String(customer.snow_tier_id) : 'custom',
      // Resolved by the server, so a deactivated tier still shows its name
      // instead of quietly reading as no request at all.
      requestedSnowTierName: customer.requested_snow_tier_name || null,
      priceSnow: customer.price_snow?.toString() || '50.00',
      saltPrice: customer.salt_price?.toString() || '0.00',
      driveway: customer.driveway ?? true,
      sidewalks: customer.sidewalks ?? false,
      walkways: customer.walkways ?? false,
      salt: customer.salt ?? false,
      // An intake signup written before the intake stored salt_preference has
      // NULL there and its answer only in the salt boolean. Reading that as
      // 'never' is what made a save clear the customer's salt; the intake maps
      // a ticked box to 'as_needed', so the fallback does too.
      saltPreference: customer.salt_preference || (customer.salt ? 'as_needed' : 'never'),
      paymentMethodPreferred: customer.payment_method_preferred || newCustomerMethod(),
      snowfallThreshold: customer.snowfall_threshold || 'any',
      servicePreference: customer.service_preference || 'on_call',
      serviceType: customer.service_type || 'snow_removal',
      referredById: customer.referred_by_id?.toString() || '',
      billingSameAsService: customer.billing_same_as_service ?? true,
      billingName: customer.billing_name || '',
      billingAddress: customer.billing_address || '',
      billingCity: customer.billing_city || '',
      billingState: customer.billing_state || '',
      billingZip: customer.billing_zip || '',
      notes: customer.notes || '',
      leadSource: customer.lead_source || '',
      discountAmount: customer.discount_amount?.toString() || '',
      discountType: customer.discount_type || '',
      discountReason: customer.discount_reason || '',
      emailSubscribed: customer.email_subscribed ?? true,
      doNotServe: customer.do_not_serve === true,
      doNotServeReason: customer.do_not_serve_reason || '',
    });
    setError('');
    setErrorIsLimit(false);
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle phone number formatting
    if (name === 'phone' || name === 'billingPhone') {
      setFormData((prev) => ({
        ...prev,
        [name]: formatPhoneInput(value),
      }));
      return;
    }

    // Handle tier changes - auto-fill prices from dynamic pricing tiers.
    // tierPrices() is the shared helper; null is "keep existing prices", which
    // is what Custom and an unknown tier id both mean.
    if (name === 'snowTierId') {
      const prices = tierPrices(value, pricingTiers);

      setFormData((prev) => ({
        ...prev,
        snowTierId: value,
        priceSnow: prices ? prices.priceSnow : prev.priceSnow,
        saltPrice: prices ? prices.saltPrice : prev.saltPrice,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setErrorIsLimit(false);

    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: stripPhone(formData.phone),
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        // Only snowTierId is sent. The server derives the legacy customers.tier
        // value from it - the tier's exact name, or 'custom' for null.
        snowTierId: formData.snowTierId === 'custom' ? null : parseInt(formData.snowTierId, 10),
        // An empty price field sends null, not 50.00.
        //
        // This was a WRITE-side copy of the same phantom $50 the list views
        // used to render, and the more damaging one. An owner confirming a
        // customer who answered "Not sure" - tier still on Custom, price still
        // empty - saved the form and silently wrote price_snow = 50.00, a number
        // nobody chose, straight into the database. The size the customer was
        // asked about would then be confirmed at a made-up price.
        //
        // null reaches the server's COALESCE($12, price_snow) and leaves the
        // column exactly as it was, so "I have not set a price yet" stays that.
        // Picking a tier still auto-fills both fields from the tier above, so
        // the ordinary path is unaffected.
        priceSnow: formData.priceSnow === '' || formData.priceSnow == null
          ? null
          : (parseFloat(formData.priceSnow) || 0),
        saltPrice: parseFloat(formData.saltPrice) || 0,
        driveway: formData.driveway,
        sidewalks: formData.sidewalks,
        walkways: formData.walkways,
        salt: formData.saltPreference === 'always',
        saltPreference: formData.saltPreference,
        paymentMethodPreferred: formData.paymentMethodPreferred,
        snowfallThreshold: formData.snowfallThreshold || 'any',
        servicePreference: formData.servicePreference || 'on_call',
        referredById: formData.referredById ? parseInt(formData.referredById) : null,
        billingSameAsService: formData.billingSameAsService,
        billingName: formData.billingSameAsService ? null : formData.billingName,
        billingAddress: formData.billingSameAsService ? null : formData.billingAddress,
        billingCity: formData.billingSameAsService ? null : formData.billingCity,
        billingState: formData.billingSameAsService ? null : formData.billingState,
        billingZip: formData.billingSameAsService ? null : formData.billingZip,
        notes: formData.notes,
        leadSource: formData.leadSource || null,
        serviceType: formData.serviceType || 'snow_removal',
        isActive: true,
        discountAmount: formData.discountType ? parseFloat(formData.discountAmount) || null : null,
        discountType: formData.discountType || null,
        discountReason: formData.discountType ? formData.discountReason : null,
        // The "Subscribed to emails" box. It was bound to formData since the
        // bulk email feature shipped but never sent, and PUT only writes keys
        // it is given, so ticking it saved nothing. POST reads it as
        // `!== false`, so an unticked box on a new customer now sticks too.
        emailSubscribed: formData.emailSubscribed !== false,
      };

      if (editingCustomer) {
        // Do Not Serve is edit-only: a new customer is never flagged. Clearing
        // the box sends a null reason, and the server nulls it regardless.
        payload.doNotServe = formData.doNotServe === true;
        payload.doNotServeReason = formData.doNotServe ? (formData.doNotServeReason || '').trim() || null : null;
        // Saving the edit form is the owner's review of an intake signup: it
        // clears "New - From Form" and the Dashboard card. Edit only, and
        // never on un-archive, which sends { isActive: true } alone.
        payload.intakeReviewed = true;
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/customers/${editingCustomer.id}`,
          payload
        );
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/customers`, payload);
      }

      setShowModal(false);
      loadCustomers();
    } catch (err) {
      // A refused create carries code CUSTOMER_LIMIT_REACHED and a message
      // naming the plan, the limit and the current count. Show it as it came,
      // and flag it so the modal can offer the way out.
      setError(err.response?.data?.error || 'Failed to save customer');
      setErrorIsLimit(err.response?.data?.code === 'CUSTOMER_LIMIT_REACHED');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (customer) => {
    if (!window.confirm(`Deactivate ${customer.first_name} ${customer.last_name}? They will be hidden but can be reactivated later.`)) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/customers/${customer.id}`);
      loadCustomers();
    } catch (err) {
      showNotification('Failed to deactivate customer', 'error');
    }
  };

  const handlePermanentDelete = async (customer) => {
    if (!window.confirm(`PERMANENTLY DELETE ${customer.first_name} ${customer.last_name}?\n\nThis cannot be undone. All customer data will be removed.`)) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/customers/${customer.id}?permanent=true`);
      loadCustomers();
      showNotification('Customer permanently deleted', 'success');
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to delete customer', 'error');
    }
  };

  // A customer held back by the plan limit. The public intake form always
  // accepts a submission, but when the company is at its customer limit the
  // row is written inactive and badged rather than dropped, so the lead is
  // kept. Held rows never count against the limit, which is what makes this
  // safe to leave sitting there.
  const isHeldByPlanLimit = (customer) =>
    customer.is_active === false && customer.intake_status === 'waitlisted';

  // Activate goes through its own endpoint rather than PUT, because it has to
  // bring the held properties back with the parent and re-run the limit check
  // server-side. The server refuses while the company is still full and says
  // so; that message is worth showing verbatim.
  const handleActivateHeld = async (customer) => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/customers/${customer.id}/activate`
      );
      const extra = data?.propertiesActivated
        ? ` and ${data.propertiesActivated} ${data.propertiesActivated === 1 ? 'property' : 'properties'}`
        : '';
      showNotification(`${customer.first_name} ${customer.last_name}${extra} activated`, 'success');
      loadCustomers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to activate customer', 'error');
    }
  };

  // Un-archive sends is_active and NOTHING ELSE. Do not add keys to this body.
  //
  // It used to spread the raw customer row, which carried every column under its
  // snake_case name. PUT /api/customers/:id read camelCase, so most of those keys
  // missed and the handler - which assigned 22 nullable columns unguarded - wrote
  // NULL over all of them. One un-archive click destroyed 17 columns: service_type,
  // service_frequency, the referral, all six billing columns, all three discount
  // columns, and it reset salt_price to 0, salt_preference to 'never',
  // snowfall_threshold to 'any' and service_preference to 'on_call'.
  //
  // The server is presence-based now, so a one-key body touches one column. That
  // also makes the tier protection deliberate rather than accidental: un-archiving
  // a customer is not agreeing with the driveway size they stated, so this request
  // must carry no tier decision. It used to carry none because snow_tier_id did not
  // match snowTierId - an ACCIDENT OF CASING. It now carries none because there is
  // nothing in the body but isActive, which is the whole point of un-archive.
  //
  // Adding snowTierId here, in any form, would silently start clearing
  // size_confirmation_pending and the New - From Form badge on every un-archive,
  // and nothing would fail loudly to tell you. The matching notes are on the flag
  // clear in server.js and on the badge condition below.
  const handleReactivate = async (customer) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/customers/${customer.id}`, {
        isActive: true,
      });
      loadCustomers();
    } catch (err) {
      // The server refuses a reactivation that would push the company past its
      // customer limit, and explains why. Swallowing that and saying "failed"
      // leaves the owner with no idea what to do about it.
      showNotification(err.response?.data?.error || 'Failed to reactivate customer', 'error');
    }
  };

  // Quick service type change handler.
  //
  // Sends serviceType and NOTHING ELSE. This body used to carry 14 keys and omit
  // 19 more, and because the server assigned every unguarded column on every
  // request, changing one enum from this dropdown wiped 20 columns on a single
  // click with no confirmation and no undo: the customer's whole service
  // specification, their billing identity and their standing discount. That was
  // never a casing accident - the body was deliberate, the statement was not.
  //
  // The server is presence-based now, so what is not sent is left alone. Keep this
  // body minimal; every key added here is a column this dropdown starts writing.
  const handleQuickServiceTypeChange = async (customer, newServiceType) => {
    setUpdatingServiceType(customer.id);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/customers/${customer.id}`, {
        serviceType: newServiceType,
      });
      loadCustomers();
    } catch (err) {
      showNotification('Failed to update service type', 'error');
    } finally {
      setUpdatingServiceType(null);
    }
  };

  // Portal invite functions
  const loadPortalStatus = async (customerId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/customers/${customerId}/portal-status`
      );
      setPortalStatus(prev => ({ ...prev, [customerId]: response.data }));
    } catch (err) {
      console.error('Failed to load portal status:', err);
    }
  };

  const handlePortalInvite = async (customer) => {
    if (!customer.email) {
      showNotification('Customer does not have an email address. Please add an email first.', 'error');
      return;
    }

    setInvitingPortal(customer.id);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/portal/invite/${customer.id}`
      );

      if (response.data.inviteSent) {
        showNotification(`Portal invitation sent to ${customer.email}!`, 'success');
      } else if (response.data.inviteUrl) {
        // Copy URL to clipboard and show message
        try {
          await navigator.clipboard.writeText(response.data.inviteUrl);
          showNotification('Email not configured. Invite link copied to clipboard.', 'info');
        } catch {
          showNotification(`Email not configured. Share this link: ${response.data.inviteUrl}`, 'info');
        }
      }

      // Refresh portal status
      loadPortalStatus(customer.id);
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to send portal invitation', 'error');
    } finally {
      setInvitingPortal(null);
    }
  };

  const getPortalStatusBadge = (customer) => {
    const status = portalStatus[customer.id];
    if (!status) return null;

    if (status.status === 'active') {
      return <span className="portal-badge active">Portal Active</span>;
    } else if (status.status === 'invited') {
      return <span className="portal-badge invited">Invite Sent</span>;
    }
    return null;
  };

  const getServiceBadges = (customer) => {
    const services = [];
    if (customer.driveway) services.push('Driveway');
    if (customer.sidewalks) services.push('Sidewalks');
    if (customer.walkways) services.push('Walkways');
    if (customer.salt) services.push('Salt');
    return services;
  };

  const toggleExpanded = (customerId) => {
    setExpandedCustomers(prev => ({
      ...prev,
      [customerId]: !prev[customerId]
    }));
  };

  const openAddPropertyModal = (customer) => {
    setPropertyParent(customer);
    setEditingProperty(null);
    // Use first pricing tier as default
    const defaultTier = pricingTiers.length > 0 ? pricingTiers[0] : null;
    setPropertyFormData({
      address: '',
      city: customer.city || '',
      state: customer.state || '',
      zip: '',
      snowTierId: defaultTier ? String(defaultTier.id) : 'custom',
      // A property the owner is adding by hand asked for nothing.
      requestedSnowTierName: null,
      priceSnow: defaultTier ? parseFloat(defaultTier.default_price).toFixed(2) : '50.00',
      saltPrice: defaultTier ? parseFloat(defaultTier.salt_price || 0).toFixed(2) : '8.00',
      // The parent's method when it can still be chosen, else the default.
      paymentMethodPreferred: methodOptionsFor('').some((o) => o.id === customer.payment_method_preferred)
        ? customer.payment_method_preferred
        : newCustomerMethod(),
      driveway: true,
      sidewalks: false,
      walkways: false,
      salt: false,
      saltPreference: 'never',
      serviceType: customer.service_type || 'snow_removal',
      notes: '',
    });
    setError('');
    setErrorIsLimit(false);
    setShowPropertyModal(true);
  };

  const openEditPropertyModal = (customer, property) => {
    setPropertyParent(customer);
    setEditingProperty(property);
    setPropertyFormData({
      address: property.address || '',
      city: property.city || '',
      state: property.state || '',
      zip: property.zip || '',
      snowTierId: property.snow_tier_id ? String(property.snow_tier_id) : 'custom',
      // Each property carries its OWN request, because intake asks per address.
      // Without this, a request recorded against property 2 would be invisible.
      requestedSnowTierName: property.requested_snow_tier_name || null,
      priceSnow: property.price_snow?.toString() || '50.00',
      saltPrice: property.salt_price?.toString() || '0.00',
      paymentMethodPreferred: property.payment_method_preferred || newCustomerMethod(),
      driveway: property.driveway ?? true,
      sidewalks: property.sidewalks ?? false,
      walkways: property.walkways ?? false,
      salt: property.salt ?? false,
      saltPreference: property.salt_preference || (property.salt ? 'as_needed' : 'never'),
      serviceType: property.service_type || 'snow_removal',
      notes: property.notes || '',
    });
    setError('');
    setErrorIsLimit(false);
    setShowPropertyModal(true);
  };

  const handlePropertyFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Auto-fill prices when tier changes using dynamic pricing tiers. Same
    // shared helper as the customer form above, so the two cannot drift.
    if (name === 'snowTierId') {
      const prices = tierPrices(value, pricingTiers);

      setPropertyFormData(prev => ({
        ...prev,
        snowTierId: value,
        priceSnow: prices ? prices.priceSnow : prev.priceSnow,
        saltPrice: prices ? prices.saltPrice : prev.saltPrice,
      }));
      return;
    }

    setPropertyFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setErrorIsLimit(false);

    try {
      const payload = {
        address: propertyFormData.address,
        city: propertyFormData.city,
        state: propertyFormData.state,
        zip: propertyFormData.zip,
        snowTierId: propertyFormData.snowTierId === 'custom' ? null : parseInt(propertyFormData.snowTierId, 10),
        // Same as the customer modal above: an empty field is not $50.00.
        priceSnow: propertyFormData.priceSnow === '' || propertyFormData.priceSnow == null
          ? null
          : (parseFloat(propertyFormData.priceSnow) || 0),
        saltPrice: parseFloat(propertyFormData.saltPrice) || 0,
        paymentMethodPreferred: propertyFormData.paymentMethodPreferred,
        driveway: propertyFormData.driveway,
        sidewalks: propertyFormData.sidewalks,
        walkways: propertyFormData.walkways,
        salt: propertyFormData.saltPreference === 'always',
        saltPreference: propertyFormData.saltPreference,
        serviceType: propertyFormData.serviceType || 'snow_removal',
        notes: propertyFormData.notes,
      };

      if (editingProperty) {
        // Update existing property
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/customers/${editingProperty.id}`,
          {
            ...payload,
            firstName: propertyParent.first_name,
            lastName: propertyParent.last_name,
            email: propertyParent.email,
            phone: propertyParent.phone,
            isActive: true,
            intakeReviewed: true,
          }
        );
      } else {
        // Create new property
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/customers/${propertyParent.id}/properties`,
          payload
        );
      }

      setShowPropertyModal(false);
      loadCustomers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProperty = async (property) => {
    if (!window.confirm(`Delete this property at ${property.address}? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/customers/${property.id}?permanent=true`);
      loadCustomers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to delete property', 'error');
    }
  };

  // Quick property service type change handler.
  //
  // Sends serviceType and NOTHING ELSE, for the same reason as the customer-row
  // dropdown above. On the property branch this body used to null notes, salt,
  // sidewalks, walkways, driveway and service_frequency, and reset salt_preference
  // to 'never', on every change of the enum.
  const handleQuickPropertyServiceTypeChange = async (property, newServiceType) => {
    setUpdatingPropertyServiceType(property.id);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/customers/${property.id}`, {
        serviceType: newServiceType,
      });
      loadCustomers();
    } catch (err) {
      showNotification('Failed to update property service type', 'error');
    } finally {
      setUpdatingPropertyServiceType(null);
    }
  };

  // Quick tier change handler, for the inline dropdown on a customer row AND on
  // a property row. One handler serves both because a property is a customers
  // row on the same table behind the same PUT, and the only thing the two
  // branches ever differed on was a spinner.
  //
  // THE BODY IS THE POINT. Same discipline as the two service-type handlers
  // above: send the keys this change means to write and nothing else, because
  // the server is presence-based and every key added here is a column this
  // dropdown starts writing.
  //
  //   a tier -> { snowTierId, priceSnow, saltPrice }
  //             writes snow_tier_id, tier, size_confirmation_pending,
  //             price_snow, salt_price. Five columns, all of them the decision.
  //
  //   Custom -> { snowTierId: null }
  //             writes snow_tier_id, tier, size_confirmation_pending. NO price
  //             keys, so the two price columns are not in the statement at all
  //             and keep whatever the owner set by hand. That is the whole
  //             meaning of Custom: no tier, your own price.
  //
  // The pending flag clears on all three of pick / change / Custom, and it
  // clears because snowTierId is present, not because of anything written here
  // - the server drives snow_tier_id and size_confirmation_pending off one
  // `provided` boolean so the two cannot disagree. Picking a size on the row is
  // the same decision as picking it in the modal, so the Size unconfirmed badge
  // goes and the dashboard count drops. See the long note on that clear in
  // server.js, which lists the paths that must NOT carry snowTierId.
  const handleQuickTierChange = async (row, newTierValue) => {
    setUpdatingTier(row.id);
    try {
      // Branch on the SELECTED VALUE, never on whether prices came back.
      // tierPrices returns null for Custom and for a tier id it cannot find,
      // and those two are not the same event: folding them together would make
      // an unrecognised tier id silently clear the tier instead of setting it.
      // A tier we cannot price still sets the tier; it just sends no prices.
      const prices = tierPrices(newTierValue, pricingTiers);
      const body = newTierValue === 'custom'
        ? { snowTierId: null }
        : {
            snowTierId: parseInt(newTierValue, 10),
            ...(prices ? {
              priceSnow: parseFloat(prices.priceSnow),
              saltPrice: parseFloat(prices.saltPrice),
            } : {}),
          };

      await axios.put(`${process.env.REACT_APP_API_URL}/api/customers/${row.id}`, body);
      // Awaited, so the row and its price are repainted from the server before
      // the select is re-enabled. The price on the row updates here; nothing
      // asks the owner to refresh the page.
      await loadCustomers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to update tier', 'error');
    } finally {
      setUpdatingTier(null);
    }
  };

  // "Confirm" beside a Size unconfirmed badge: accepts the tier the row holds
  // now, through the same PUT and the same snowTierId key as the dropdown and
  // the edit form. See utils/sizeConfirm.js. Shares updatingTier with the
  // dropdown, so the two cannot be in flight on one row at once.
  const handleConfirmSize = async (row) => {
    setUpdatingTier(row.id);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/customers/${row.id}`, sizeConfirmBody(row));
      // Reloaded, so the badge goes and the Size unconfirmed only list drops
      // the row from the server's own answer.
      await loadCustomers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to confirm size', 'error');
    } finally {
      setUpdatingTier(null);
    }
    // Confirming an intake signup's size sends the welcome after the server
    // answers (services/customerWelcome.js), so its badge lands a moment
    // later: one more reload picks it up.
    if (row.from_intake_form) setTimeout(() => { loadCustomers(); }, 3000);
  };

  // "Send welcome" in the card's actions: POST /api/customers/:id/welcome,
  // with force when one already went. See utils/welcome.js.
  const handleSendWelcome = async (customer) => {
    if (!window.confirm(welcomeConfirmText(customer))) return;
    setSendingWelcome(customer.id);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/customers/${customer.id}/welcome`, welcomeBody(customer));
      showNotification(res.data.channel === 'both' ? 'Welcome sent by email and text' : res.data.channel === 'sms' ? 'Welcome sent by text' : 'Welcome sent by email', 'success');
      await loadCustomers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to send the welcome', 'error');
    } finally {
      setSendingWelcome(null);
    }
  };

  // The season answer, recorded by hand from the inbox:
  // POST /api/customers/:id/season-status. Declined and the undo (pending)
  // save at once; Confirmed opens the chooser first. See utils/seasonConfirm.js.
  const saveSeasonStatus = async (customer, body) => {
    setSavingSeason(customer.id);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/customers/${customer.id}/season-status`, body);
      showNotification(seasonSavedMessage(res.data.status, res.data), 'success');
      setSeasonChooser(null);
      await loadCustomers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to save the season answer', 'error');
    } finally {
      setSavingSeason(null);
    }
  };

  const openSeasonChooser = (customer) => {
    setSeasonChooser({ customerId: customer.id, ...chooserDefaults(customer) });
  };

  return (
    <div className="customers-page">
      {/* Notification Toast */}
      {notification.show && (
        <div className={`notification-toast notification-${notification.type}`} role="alert">
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ show: false, message: '', type: 'info' })} aria-label="Dismiss">&times;</button>
        </div>
      )}

      {/* Header */}
      <StaffHeader active="customers" />

      <PullToRefreshIndicator />

      <div className="page-container">
        {/* Page Header */}
        <div className="page-header flex-between">
          <div>
            <h1 className="page-title">Customers</h1>
            <p className="text-muted">{filteredCustomers.length} customers</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {/* Every role: drivers are who the paper list is for. Prices on
                it follow shouldHidePrices, same as the cards. */}
            {!loading && printCustomers.length > 0 && (
              <button className="btn btn-outline" onClick={openPrintView}>
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z"/>
                </svg>
                Print List
              </button>
            )}
            {isAdmin() && isPro() && (
              <button className="btn btn-outline" onClick={() => setShowEmailModal(true)}>
                Message Customers
              </button>
            )}
            {/* The sends read back: the log routes allow a lapsed Pro
                company to read, as the Texts section on the cards does. */}
            {isAdmin() && (isPro() || isLapsed()) && (
              <button className="btn btn-outline" onClick={() => setShowMessageHistory(true)}>
                Message History
              </button>
            )}
            {/* Owner and admin, any plan: the invite route has no tier gate. */}
            {isAdmin() && (
              <button className="btn btn-outline" onClick={() => setShowBulkInvite(true)}>
                Invite all to portal
              </button>
            )}
            {isAdmin() && (
              <button className="btn btn-primary" onClick={openAddModal}>
                + Add Customer
              </button>
            )}
          </div>
        </div>

        {/* The Dashboard's new-signups notice, so an owner working from this
            list sees a signup without going back there. */}
        {isAdmin() && (
          <IntakeSignupsNotice
            items={intakeSignups}
            error={intakeSignupsError}
            reviewingId={intakeReviewingId}
            onReview={reviewIntakeSignup}
            onMarkReviewed={handleMarkIntakeReviewed}
          />
        )}

        {/* On-call customers who asked for service in the portal since the
            notice was last dismissed (components/ServiceRequestsNotice.js). */}
        {isAdmin() && (
          <ServiceRequestsNotice
            notice={serviceRequestsNotice}
            dismissing={dismissingServiceRequests}
            error={serviceRequestsNoticeError}
            onDismiss={dismissServiceRequests}
          />
        )}

        {/* Filters */}
        <div className="filters-bar">
          <input
            type="text"
            className="form-input search-input"
            placeholder="Search by name, address, email..."
            value={search}
            onChange={handleSearch}
          />
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive only
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={doNotServeOnly}
              onChange={(e) => setDoNotServeOnly(e.target.checked)}
            />
            Do not serve only
          </label>
          {!shouldHidePrices() && (
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={owesOnly}
                onChange={(e) => setOwesOnly(e.target.checked)}
              />
              Owes money only
            </label>
          )}
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={sizeUnconfirmedOnly}
              onChange={(e) => setSizeUnconfirmedOnly(e.target.checked)}
            />
            Size unconfirmed only
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={newSignupsOnly}
              onChange={(e) => setNewSignupsOnly(e.target.checked)}
            />
            New signups only
          </label>
          <label className="checkbox-label">
            Season
            <select
              className="form-select season-filter-select"
              value={seasonFilter}
              onChange={(e) => setSeasonFilter(e.target.value)}
              aria-label="Season answer"
            >
              {SEASON_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </label>
        </div>

        {heldCount > 0 && !showInactive && (
          <div className="held-banner" role="status">
            <strong>
              {heldCount} {heldCount === 1 ? 'signup is' : 'signups are'} held because your plan is
              full.
            </strong>{' '}
            They came in through your intake form and were kept rather than turned away. They do
            not count against your limit. Upgrade your plan or archive a customer you no longer
            serve, then click Activate on each one.
          </div>
        )}

        {/* Customer List */}
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: '3rem', marginBottom: '12px', opacity: 0.5 }}>👥</div>
            <h3 style={{ margin: '0 0 8px', color: 'var(--color-text)' }}>No customers yet</h3>
            <p style={{ color: 'var(--color-text-light)', margin: '0 0 16px' }}>Add your first customer to get started</p>
            {isAdmin() && (
              <button className="btn btn-primary" onClick={openAddModal}>
                + Add Customer
              </button>
            )}
          </div>
        ) : (
          <div className="customer-list">
            {filteredCustomers.map((customer) => {
              // For SnowTrack: if customer is lawn-only, display first snow property's address/price
              const isCustomerSnowEligible = ['snow_removal', 'both'].includes(customer.service_type);
              const displayInfo = isCustomerSnowEligible
                ? customer
                : (customer.properties?.[0] || customer);

              // If displaying property as main, exclude it from additional properties list
              const remainingProperties = !isCustomerSnowEligible && customer.properties?.length > 0
                ? customer.properties.slice(1)
                : customer.properties || [];

              return (
              <div
                key={customer.id}
                id={`customer-card-${customer.id}`}
                className={`customer-card ${!customer.is_active ? 'inactive' : ''} ${highlightedId === `customer-card-${customer.id}` ? 'focus-highlight' : ''}`}
              >
                <div className="customer-card-header">
                  <div className="customer-info">
                    <h3 className="customer-name">
                      {customer.first_name} {customer.last_name}
                      {isHeldByPlanLimit(customer) ? (
                        <span className="held-badge" title="This signup arrived while your plan was full. It is held for you, it does not count against your limit, and Activate takes it on.">
                          Held: plan limit reached
                        </span>
                      ) : (
                        !customer.is_active && <span className="inactive-badge">Inactive</span>
                      )}
                      {/* "New - From Form" shows until the owner REVIEWS the
                          signup: saves its edit form, or presses Mark reviewed
                          on the Dashboard's new-signups card. Both stamp
                          intake_reviewed_at (migration 078), and the card lists
                          exactly the parents this test passes, so the two
                          cannot disagree.

                          Un-archive, deactivate, the inline service-type select
                          and taking a held signup off the waitlist do not
                          review: none of them sends intakeReviewed.

                          This replaced a rule that derived the review from
                          snow_tier_id. The intake now writes the tier the
                          customer picked, so that rule hid the badge from most
                          signups the moment they arrived.

                          !size_confirmation_pending still makes the two badges
                          mutually exclusive. While the size is unconfirmed the
                          size badge names the action and carries what the
                          customer said, so it holds the row alone. */}
                      {showsNewSignupBadge(customer) && (
                        <span className="intake-badge">New - From Form{customer.created_at ? ` (${new Date(customer.created_at).toLocaleDateString('en-US', inZone(timezone, { month: 'short', day: 'numeric' }))})` : ''}</span>
                      )}
                      {customer.size_confirmation_pending && (
                        <span
                          className="size-pending-badge"
                          title={
                            customer.requested_snow_tier_name
                              ? `Customer said: ${customer.requested_snow_tier_name}. Press Confirm to accept the size shown, or pick another.`
                              : 'The customer did not know their driveway size. Pick one, or press Confirm to accept the size shown.'
                          }
                        >
                          Size unconfirmed
                        </span>
                      )}
                      {customer.size_confirmation_pending && isAdmin() && (
                        <SizeConfirmButton
                          row={customer}
                          disabled={updatingTier === customer.id}
                          onConfirm={handleConfirmSize}
                        />
                      )}
                      {customer.do_not_serve && (
                        <DoNotServeBadge reason={customer.do_not_serve_reason} />
                      )}
                      <SmsConsentBadge row={customer} />
                      {seasonBadge(customer) && (
                        <span className={seasonBadge(customer).className} title="This customer's answer for the season (Dashboard, Season)">
                          {seasonBadge(customer).label}
                        </span>
                      )}
                      {customer.season_status === 'pending' && isAdmin() && (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline season-answer-btn"
                            onClick={() => openSeasonChooser(customer)}
                            disabled={savingSeason === customer.id}
                          >
                            Confirmed
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline season-answer-btn"
                            onClick={() => saveSeasonStatus(customer, { status: 'declined' })}
                            disabled={savingSeason === customer.id}
                          >
                            Declined
                          </button>
                        </>
                      )}
                      {welcomeLabel(customer, timezone) && (
                        <span className="welcome-badge" title="The welcome message this customer received (Settings, Welcome Message)">
                          {welcomeLabel(customer, timezone)}
                        </span>
                      )}
                      {remainingProperties.length > 0 && (
                        <span className="property-count-badge">
                          {remainingProperties.length} {remainingProperties.length === 1 ? 'property' : 'properties'}
                        </span>
                      )}
                      {getPortalStatusBadge(customer)}
                    </h3>
                    <p className="customer-address">{displayInfo.address}</p>
                    <p className="customer-city">{displayInfo.city}, {displayInfo.state} {displayInfo.zip}</p>
                  </div>
                  {!shouldHidePrices() && (
                    <div className="customer-price">
                      {!hasStopPrice(displayInfo) ? (
                        /* No price set, so no discount pair either: a percentage
                           off a price nobody has chosen has nothing to apply to,
                           and showing one would imply a base that does not
                           exist. */
                        <span className="price-unset">{NO_PRICE_LABEL}</span>
                      ) : displayInfo.discount_amount && displayInfo.discount_type ? (
                        <>
                          <span className="price-original">
                            {formatStopTotal(displayInfo, displayInfo.salt)}
                          </span>
                          <span className="price-discounted">
                            {formatCurrency(calculateDiscountedPrice(
                              stopTotal(displayInfo, displayInfo.salt),
                              parseFloat(displayInfo.discount_amount),
                              displayInfo.discount_type
                            ))}
                          </span>
                        </>
                      ) : (
                        formatStopTotal(displayInfo, displayInfo.salt)
                      )}
                      {displayInfo.salt && <span className="salt-badge-small">Salt</span>}
                      {/* What is owed across the customer and all its
                          properties, from the locked stop totals. Hidden at
                          zero; unpriced stops are counted, never added as $0. */}
                      {(() => {
                        const b = balanceOf(customer);
                        if (!b || !(b.owed > 0 || b.unpricedCount > 0 || b.pendingDepthCount > 0)) return null;
                        return (
                          <span className="balance-owed">
                            {b.owed > 0 && <>Owes {formatCurrency(b.owed)}</>}
                            {b.owed > 0 && b.unpricedCount > 0 && ' · '}
                            {b.unpricedCount > 0 && (
                              <span className="balance-unpriced">{b.unpricedCount} unpriced</span>
                            )}
                            {(b.owed > 0 || b.unpricedCount > 0) && b.pendingDepthCount > 0 && ' · '}
                            {b.pendingDepthCount > 0 && (
                              <span className="balance-unpriced">{b.pendingDepthCount} awaiting depth</span>
                            )}
                          </span>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {seasonChooser && seasonChooser.customerId === customer.id && (
                  <div className="season-chooser" role="group" aria-label="Confirm for the season">
                    <span className="season-chooser-title">Confirmed for {customer.season_key}:</span>
                    <select
                      className="form-select"
                      aria-label="Service"
                      value={seasonChooser.servicePreference}
                      onChange={(e) => setSeasonChooser((prev) => ({ ...prev, servicePreference: e.target.value }))}
                    >
                      <option value="seasonal">Seasonal</option>
                      <option value="on_call">On-call</option>
                    </select>
                    <select
                      className="form-select"
                      aria-label="Salt"
                      value={seasonChooser.wantsSalt ? 'yes' : 'no'}
                      onChange={(e) => setSeasonChooser((prev) => ({ ...prev, wantsSalt: e.target.value === 'yes' }))}
                    >
                      <option value="yes">Salt: Yes</option>
                      <option value="no">Salt: No</option>
                    </select>
                    <select
                      className="form-select"
                      aria-label="Size"
                      value={seasonChooser.snowTierId}
                      onChange={(e) => setSeasonChooser((prev) => ({ ...prev, snowTierId: e.target.value }))}
                    >
                      <option value="">Size: as now ({customer.snow_tier_name || 'Custom'})</option>
                      {pricingTiers.map((tier) => (
                        <option key={tier.id} value={String(tier.id)}>{tier.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => saveSeasonStatus(customer, confirmBody(seasonChooser))}
                      disabled={savingSeason === customer.id}
                    >
                      {savingSeason === customer.id ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline" onClick={() => setSeasonChooser(null)}>
                      Cancel
                    </button>
                  </div>
                )}

                {!shouldHidePrices() && displayInfo.discount_amount && displayInfo.discount_type && (
                  <div className="discount-info-row">
                    <span className="discount-label">Discount:</span>
                    <span className="discount-value">
                      {displayInfo.discount_type === 'percent' ? `${displayInfo.discount_amount}% off` : `${formatCurrency(displayInfo.discount_amount)} off`}
                    </span>
                    {displayInfo.discount_reason && (
                      <span className="discount-reason">- {displayInfo.discount_reason}</span>
                    )}
                  </div>
                )}

                <div className="customer-card-body">
                  {customer.phone && (
                    <a href={`tel:${customer.phone}`} className="customer-phone">
                      {formatPhone(customer.phone)}
                    </a>
                  )}
                  {/* The parent's email, never displayInfo's: email is a
                      customer-level field, and properties carry none. */}
                  {customer.email ? (
                    <a href={`mailto:${customer.email}`} className="customer-email">
                      {customer.email}
                    </a>
                  ) : isAdmin() && (
                    // Explains why Invite to Portal is missing below.
                    <p className="customer-email-missing">No email</p>
                  )}
                  {/* Email off, by the customer's own unsubscribe link or the
                      edit form (migration 112). */}
                  {unsubscribedLine(customer, timezone) && (
                    <p className="customer-email-missing" data-testid="customer-unsubscribed">
                      {unsubscribedLine(customer, timezone)}
                    </p>
                  )}
                  {customer.referred_by_name && (
                    <p className="referred-by-text">Referred by: {customer.referred_by_name}</p>
                  )}
                  <div className="service-badges">
                    {/* Service Type Badge with Quick Edit - uses displayInfo for property-as-main scenarios */}
                    {isAdmin() ? (
                      <select
                        className={`service-type-select ${
                          displayInfo.service_type === 'lawn_care' ? 'lawn' :
                          displayInfo.service_type === 'both' ? 'both' : 'snow'
                        }`}
                        value={displayInfo.service_type || 'snow_removal'}
                        onChange={(e) => isCustomerSnowEligible
                          ? handleQuickServiceTypeChange(customer, e.target.value)
                          : handleQuickPropertyServiceTypeChange(displayInfo, e.target.value)
                        }
                        disabled={isCustomerSnowEligible
                          ? updatingServiceType === customer.id
                          : updatingPropertyServiceType === displayInfo.id
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="snow_removal">Snow</option>
                        <option value="lawn_care">Lawn</option>
                        <option value="both">Both</option>
                      </select>
                    ) : (
                      <span className={`service-type-badge ${
                        displayInfo.service_type === 'lawn_care' ? 'lawn' :
                        displayInfo.service_type === 'both' ? 'both' : 'snow'
                      }`}>
                        {displayInfo.service_type === 'lawn_care' ? 'Lawn' :
                         displayInfo.service_type === 'both' ? 'Both' : 'Snow'}
                      </span>
                    )}
                    {/* The badge reads the snow_tier_id foreign key, resolved to
                        a name by the server, not the legacy customers.tier text.
                        That text still holds whatever it held historically - a
                        stringified tier id, a lowercase legacy name, or 'custom'
                        - which is why a customer correctly linked to Large could
                        show a badge reading 12. No tier means Custom pricing, so
                        the badge says so rather than disappearing.
                        Not title-cased: the name is whatever the owner typed in
                        Settings, and forcing case would turn X-Large into
                        X-large. */}
                    {/* Editable in place for an admin, same as the service type
                        pill beside it; the read-only badge stays for everyone
                        else. Bound to displayInfo, the row the badge was
                        already describing, so on a lawn-only customer showing
                        its first snow property this edits THAT property and not
                        the parent. No isCustomerSnowEligible branch is needed,
                        unlike the service-type select above: one handler takes
                        whichever row it is handed. */}
                    {isAdmin() ? (
                      <TierQuickSelect
                        row={displayInfo}
                        tiers={pricingTiers}
                        className="tier-select"
                        disabled={updatingTier === displayInfo.id}
                        onChange={handleQuickTierChange}
                      />
                    ) : (
                      <span className={`tier-badge ${displayInfo.snow_tier_name ? '' : 'custom'}`}>
                        {displayInfo.snow_tier_name || 'Custom'}
                      </span>
                    )}
                    <span className={`frequency-badge ${displayInfo.service_preference === 'seasonal' ? 'seasonal' : 'as-needed'}${displayInfo.service_preference !== 'seasonal' && onCallPremium ? ' on-call-premium' : ''}`}>
                      {displayInfo.service_preference === 'seasonal' ? 'Seasonal' : onCallLabel}
                    </span>
                    {displayInfo.snowfall_threshold && displayInfo.snowfall_threshold !== 'any' && (
                      <span className="threshold-badge">{displayInfo.snowfall_threshold}"+ snow</span>
                    )}
                    {/* Add-ons are kept on the root customer, so a property
                        shown here wears its customer's. */}
                    <CustomerAddonBadges addons={customer.customer_addons} />
                    {getServiceBadges(displayInfo).map((service) => (
                      <span key={service} className="service-badge">{service}</span>
                    ))}
                    {customer.referral_count > 0 && (
                      <span className="referral-badge">{customer.referral_count} referral{customer.referral_count !== 1 ? 's' : ''}</span>
                    )}
                    {customer.lead_source && (
                      <span className="lead-source-badge">
                        {leadSources.find(s => s.id === customer.lead_source)?.name || customer.lead_source}
                      </span>
                    )}
                  </div>
                  {showTexts && <CustomerTexts customerId={customer.id} canSend={isPro() && !isLapsed()} />}
                  {/* On-call only: requests are kept on this root customer
                      and cover its properties. Seasonal customers are served
                      every storm, so there is nothing to request. */}
                  {isAdmin() && customer.service_preference !== 'seasonal' && (
                    <CustomerServiceRequests customerId={customer.id} />
                  )}
                  {isAdmin() && (
                    <CustomerAddons
                      customerId={customer.id}
                      addons={customer.customer_addons}
                      onSaved={(addons) => setCustomers((prev) => prev.map((c) => (
                        c.id === customer.id ? { ...c, customer_addons: addons } : c
                      )))}
                    />
                  )}
                </div>

                <div className="customer-card-footer">
                  <div className="payment-method">
                    {getPaymentMethodName(displayInfo.payment_method_preferred)}
                  </div>
                  {isAdmin() && (
                    <div className="customer-actions">
                      <button
                        className="btn btn-sm btn-property"
                        onClick={() => openAddPropertyModal(customer)}
                        title="Add another property for this customer"
                      >
                        + Property
                      </button>
                      <button className="btn btn-sm btn-outline" onClick={() => openEditModal(customer)} aria-label="Edit customer">
                        Edit
                      </button>
                      {!isHeldByPlanLimit(customer) && (
                        <button className="btn btn-sm btn-outline" onClick={() => openStatement(customer)}>
                          Statement
                        </button>
                      )}
                      {customer.email && (
                        <button
                          className="btn btn-sm btn-portal"
                          onClick={() => handlePortalInvite(customer)}
                          disabled={invitingPortal === customer.id || portalStatus[customer.id]?.status === 'active'}
                          title={portalStatus[customer.id]?.status === 'active' ? 'Customer already has portal access' : 'Invite customer to portal'}
                        >
                          {invitingPortal === customer.id ? 'Sending...' :
                           portalStatus[customer.id]?.status === 'active' ? 'Portal Active' :
                           portalStatus[customer.id]?.status === 'invited' ? 'Resend Invite' : 'Invite to Portal'}
                        </button>
                      )}
                      {!customer.do_not_serve && (
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => handleSendWelcome(customer)}
                          disabled={sendingWelcome === customer.id}
                          title="Send the welcome message from Settings"
                        >
                          {sendingWelcome === customer.id ? 'Sending...' : welcomeActionLabel(customer)}
                        </button>
                      )}
                      {(customer.season_status === 'confirmed' || customer.season_status === 'declined') && (
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => saveSeasonStatus(customer, { status: 'pending' })}
                          disabled={savingSeason === customer.id}
                          title="Undo the season answer: back to No reply. Choices already saved to the card stay."
                        >
                          Undo {customer.season_status === 'confirmed' ? 'Confirmed' : 'Declined'}
                        </button>
                      )}
                      {customer.is_active ? (
                        <button className="btn btn-sm btn-outline" onClick={() => handleDeactivate(customer)}>
                          Deactivate
                        </button>
                      ) : isHeldByPlanLimit(customer) ? (
                        <button className="btn btn-sm btn-success" onClick={() => handleActivateHeld(customer)}>
                          Activate
                        </button>
                      ) : (
                        <button className="btn btn-sm btn-success" onClick={() => handleReactivate(customer)}>
                          Reactivate
                        </button>
                      )}
                      <button className="btn btn-sm btn-danger" onClick={() => handlePermanentDelete(customer)}>
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                {/* Expand/Collapse for properties */}
                {remainingProperties.length > 0 && (
                  <>
                    <button
                      className="properties-toggle"
                      onClick={() => toggleExpanded(customer.id)}
                    >
                      {expandedCustomers[customer.id] ? '▼' : '▶'} {remainingProperties.length} Additional {remainingProperties.length === 1 ? 'Property' : 'Properties'}
                    </button>

                    {expandedCustomers[customer.id] && (
                      <div className="properties-list">
                        {remainingProperties.map((property) => (
                          <div
                            key={property.id}
                            id={`property-card-${property.id}`}
                            className={`property-card ${highlightedId === `property-card-${property.id}` ? 'focus-highlight' : ''}`}
                          >
                            <div className="property-info">
                              <p className="property-address">{property.address}</p>
                              <p className="property-city">{property.city}, {property.state} {property.zip}</p>
                              <div className="property-services">
                                {/* Property Service Type Badge/Select */}
                                {isAdmin() ? (
                                  <select
                                    className={`service-type-select ${
                                      property.service_type === 'lawn_care' ? 'lawn' :
                                      property.service_type === 'both' ? 'both' : 'snow'
                                    }`}
                                    value={property.service_type || 'snow_removal'}
                                    onChange={(e) => handleQuickPropertyServiceTypeChange(property, e.target.value)}
                                    disabled={updatingPropertyServiceType === property.id}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value="snow_removal">Snow</option>
                                    <option value="lawn_care">Lawn</option>
                                    <option value="both">Both</option>
                                  </select>
                                ) : (
                                  <span className={`service-type-badge ${
                                    property.service_type === 'lawn_care' ? 'lawn' :
                                    property.service_type === 'both' ? 'both' : 'snow'
                                  }`}>
                                    {property.service_type === 'lawn_care' ? 'Lawn' :
                                     property.service_type === 'both' ? 'Both' : 'Snow'}
                                  </span>
                                )}
                                {/* Same foreign-key badge as the customer row.
                                    Properties are customers rows, so each one
                                    carries its own snow_tier_id. */}
                                {isAdmin() ? (
                                  <TierQuickSelect
                                    row={property}
                                    tiers={pricingTiers}
                                    className="tier-select-sm"
                                    disabled={updatingTier === property.id}
                                    onChange={handleQuickTierChange}
                                  />
                                ) : (
                                  <span className={`tier-badge-sm ${property.snow_tier_name ? '' : 'custom'}`}>
                                    {property.snow_tier_name || 'Custom'}
                                  </span>
                                )}
                                {/* And its own pending flag. One property can be
                                    unconfirmed while another on the same
                                    customer is settled, because the flag lives
                                    on each row. This badge is why the children
                                    query has to name size_confirmation_pending
                                    in its explicit column list. */}
                                {property.size_confirmation_pending && (
                                  <span
                                    className="size-pending-badge"
                                    title={
                                      property.requested_snow_tier_name
                                        ? `Customer said: ${property.requested_snow_tier_name}. Press Confirm to accept the size shown, or pick another.`
                                        : 'The customer did not know this driveway size. Pick one, or press Confirm to accept the size shown.'
                                    }
                                  >
                                    Size unconfirmed
                                  </span>
                                )}
                                {property.size_confirmation_pending && isAdmin() && (
                                  <SizeConfirmButton
                                    row={property}
                                    disabled={updatingTier === property.id}
                                    onConfirm={handleConfirmSize}
                                  />
                                )}
                                {/* Inherited from the customer, resolved by the
                                    server onto each property row. */}
                                {property.do_not_serve && (
                                  <DoNotServeBadge reason={property.do_not_serve_reason} inherited />
                                )}
                                <SmsConsentBadge row={property} inherited />
                                {property.driveway && <span className="service-badge-sm">Driveway</span>}
                                {property.sidewalks && <span className="service-badge-sm">Sidewalks</span>}
                                {property.walkways && <span className="service-badge-sm">Walkways</span>}
                                {property.salt && <span className="service-badge-sm salt">Salt</span>}
                              </div>
                            </div>
                            <div className="property-pricing">
                              <div className={`property-price${hasStopPrice(property) ? '' : ' price-unset'}`}>
                                {formatStopTotal(property, property.salt)}
                              </div>
                              <div className="property-payment">
                                {getPaymentMethodName(property.payment_method_preferred)}
                              </div>
                            </div>
                            {isAdmin() && (
                              <div className="property-actions">
                                <button
                                  className="btn btn-xs btn-outline"
                                  onClick={() => openEditPropertyModal(customer, property)}
                                  aria-label="Edit property"
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-xs btn-danger"
                                  onClick={() => handleDeleteProperty(property)}
                                  aria-label="Delete property"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Print View: portaled to body so print CSS can display:none the app
          (#root) outright; a hidden-but-laid-out page padded the printout. */}
      {showPrintView && createPortal(
        <div className="print-overlay">
          <div className="print-container">
            <div className="print-header no-print">
              <h2>Print Customer List</h2>
              <div className="print-actions">
                {/* Notes hold gate codes and special instructions in one
                    field, so the owner decides per print. Off every time the
                    view opens. */}
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={printIncludeNotes}
                    onChange={(e) => setPrintIncludeNotes(e.target.checked)}
                  />
                  Include notes
                </label>
                <button className="btn btn-primary" onClick={handlePrint}>
                  <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z"/>
                  </svg>
                  Print
                </button>
                <button className="btn btn-secondary" onClick={() => setShowPrintView(false)}>
                  Close
                </button>
              </div>
            </div>

            <div className="print-content">
              <div className="print-page-header">
                <h1>{user?.companyName || 'Customer List'}</h1>
                <p className="print-date">Printed: {new Date().toLocaleDateString('en-US', inZone(timezone, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }))}</p>
                <p className="print-count">
                  {printCustomers.length} {printCustomers.length === 1 ? 'customer' : 'customers'}
                </p>
                <p className="print-filter">{getPrintFilterLabel()}</p>
              </div>

              <div className="print-customer-list">
                {printCustomers.map((customer, index) => {
                  // Same row choice as the card: a lawn-only customer shows
                  // its first property, which then is not repeated below.
                  const isCustomerSnowEligible = ['snow_removal', 'both'].includes(customer.service_type);
                  const displayInfo = isCustomerSnowEligible
                    ? customer
                    : (customer.properties?.[0] || customer);
                  const remainingProperties = !isCustomerSnowEligible && customer.properties?.length > 0
                    ? customer.properties.slice(1)
                    : customer.properties || [];

                  return (
                    <div key={customer.id} className="print-customer-card">
                      <div className="print-customer-header">
                        <span className="print-customer-number">{index + 1}</span>
                        <h3 className="print-customer-name">
                          {customer.first_name} {customer.last_name}
                          {/* The marker only. The reason never goes on paper. */}
                          {customer.do_not_serve && <span className="print-dns">DO NOT SERVE</span>}
                        </h3>
                        {!shouldHidePrices() && (
                          <span className="print-customer-price">{getPrintPrice(displayInfo)}</span>
                        )}
                      </div>

                      <div className="print-customer-details">
                        <div className="print-detail-row">
                          <span className="print-label">Address:</span>
                          <span className="print-value">
                            {displayInfo.address}, {displayInfo.city}, {displayInfo.state} {displayInfo.zip}
                          </span>
                        </div>

                        {customer.phone && (
                          <div className="print-detail-row">
                            <span className="print-label">Phone:</span>
                            <span className="print-value">{formatPhone(customer.phone)}</span>
                          </div>
                        )}

                        {customer.email && (
                          <div className="print-detail-row">
                            <span className="print-label">Email:</span>
                            <span className="print-value">{customer.email}</span>
                          </div>
                        )}

                        <div className="print-detail-row">
                          <span className="print-label">Tier:</span>
                          <span className="print-value">{displayInfo.snow_tier_name || 'Custom'}</span>
                        </div>

                        <div className="print-detail-row">
                          <span className="print-label">Services:</span>
                          <span className="print-value">{getPrintServices(displayInfo)}</span>
                        </div>

                        <div className="print-detail-row">
                          <span className="print-label">Salt:</span>
                          <span className="print-value">{displayInfo.salt ? 'Yes' : 'No'}</span>
                        </div>

                        <div className="print-detail-row">
                          <span className="print-label">Service:</span>
                          <span className="print-value">{getPrintPreference(displayInfo)}</span>
                        </div>

                        {displayInfo.payment_method_preferred && (
                          <div className="print-detail-row">
                            <span className="print-label">Payment:</span>
                            <span className="print-value">{getPaymentMethodName(displayInfo.payment_method_preferred)}</span>
                          </div>
                        )}

                        {printIncludeNotes && displayInfo.notes && (
                          <div className="print-detail-row print-notes">
                            <span className="print-label">Notes:</span>
                            <span className="print-value">{displayInfo.notes}</span>
                          </div>
                        )}
                      </div>

                      {remainingProperties.length > 0 && (
                        <div className="print-properties">
                          <span className="print-properties-label">Additional Properties:</span>
                          {remainingProperties.map((prop, propIdx) => (
                            <div key={prop.id} className="print-property">
                              <div className="print-property-address">
                                {propIdx + 1}. {prop.address}, {prop.city}, {prop.state} {prop.zip}
                                {prop.do_not_serve && <span className="print-dns">DO NOT SERVE</span>}
                              </div>
                              <div className="print-property-details">
                                Tier: {prop.snow_tier_name || 'Custom'}
                                {!shouldHidePrices() && <> · Price: {getPrintPrice(prop)}</>}
                                {' · '}Services: {getPrintServices(prop)}
                                {' · '}Salt: {prop.salt ? 'Yes' : 'No'}
                              </div>
                              {printIncludeNotes && prop.notes && (
                                <div className="print-property-details print-notes-inline">
                                  Notes: {prop.notes}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="print-footer">
                <p>Generated by {user?.companyName || 'SnowTrack™'}</p>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Statement View: the same portal and print CSS as Print List, so the
          printout ends at the last line. Every figure is the server's, read
          from each stop's price lock; nothing here does arithmetic. */}
      {statementCustomer && createPortal(
        <div className="print-overlay">
          <div className="print-container statement-container">
            <div className="print-header no-print statement-controls">
              <h2>Statement: {statementCustomer.first_name} {statementCustomer.last_name}</h2>
              <div className="print-actions statement-actions">
                <select
                  className="form-input statement-period"
                  value={statementPeriod}
                  onChange={(e) => changeStatementPeriod(e.target.value)}
                  aria-label="Statement period"
                >
                  <option value="season">This season</option>
                  <option value="all">All services</option>
                  <option value="custom">Custom dates</option>
                </select>
                {statementPeriod === 'custom' && (
                  <>
                    <input
                      type="date"
                      className="form-input"
                      value={statementStart}
                      onChange={(e) => setStatementStart(e.target.value)}
                      aria-label="From"
                    />
                    <input
                      type="date"
                      className="form-input"
                      value={statementEnd}
                      onChange={(e) => setStatementEnd(e.target.value)}
                      aria-label="To"
                    />
                    <button
                      className="btn btn-outline"
                      onClick={() => loadStatement(statementCustomer.id, 'custom', statementStart, statementEnd)}
                    >
                      Show
                    </button>
                  </>
                )}
                <button className="btn btn-primary" onClick={handlePrint} disabled={!statement || statementLoading}>
                  Print
                </button>
                <button className="btn btn-secondary" onClick={closeStatement}>
                  Close
                </button>
              </div>
            </div>

            {statement && (statement.unpricedCount > 0 || statement.openingUnpricedCount > 0) && (
              <div className="statement-warning no-print" role="alert">
                {statement.unpricedCount + statement.openingUnpricedCount === 1
                  ? '1 service has no price set'
                  : `${statement.unpricedCount + statement.openingUnpricedCount} services have no price set`}
                {' '}and {statement.unpricedCount + statement.openingUnpricedCount === 1 ? 'is' : 'are'} not
                included in the totals. Set the price and re-complete the stop before printing this
                statement for anyone else.
              </div>
            )}

            {statementLoading && (
              <div className="loading-container"><div className="loading-spinner"></div></div>
            )}
            {statementError && <div className="alert alert-error">{statementError}</div>}

            {statement && !statementLoading && (
              <div className="print-content statement">
                {/* Printed too: the PDF must say that some work has no charge yet. */}
                {statementPendingDepthCount(statement) > 0 && (
                  <div className="statement-warning statement-pending-depth-banner" role="alert">
                    {statementPendingDepthText(statement)}
                  </div>
                )}
                <div className="statement-head">
                  <div className="statement-company">
                    <div className="statement-company-name">{statement.company.name}</div>
                    {statement.company.address ? (
                      <div className="statement-pre">{statement.company.address}</div>
                    ) : (
                      <div className="statement-missing">Business address not set (Settings)</div>
                    )}
                    {statement.company.phone && <div>{formatPhone(statement.company.phone)}</div>}
                    {statement.company.email && <div>{statement.company.email}</div>}
                  </div>
                  <div className="statement-title-block">
                    <div className="statement-title">STATEMENT</div>
                    <div>{statementPeriodText(statement)}</div>
                    <div className="statement-generated">Prepared {statement.generatedText}</div>
                  </div>
                </div>

                <div className="statement-billto">
                  <div className="statement-label">Customer</div>
                  <div className="statement-billto-name">{statement.customer.name}</div>
                  {statement.customer.address && <div>{statement.customer.address}</div>}
                  <div>
                    {[statement.customer.city, statement.customer.state].filter(Boolean).join(', ')}
                    {statement.customer.zip ? ` ${statement.customer.zip}` : ''}
                  </div>
                </div>

                <table className="statement-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Service</th>
                      <th className="num">Price</th>
                      <th className="num">Discount</th>
                      <th className="num">Charged</th>
                      <th className="num">Paid</th>
                      <th className="num">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statement.period.start && (
                      <tr className="statement-opening">
                        <td>{statement.period.startText}</td>
                        <td colSpan={5}>
                          Balance brought forward
                          {statement.openingUnpricedCount > 0 && (
                            <div className="statement-note">
                              Excludes {statement.openingUnpricedCount} earlier
                              {statement.openingUnpricedCount === 1 ? ' service' : ' services'} with no price set.
                            </div>
                          )}
                        </td>
                        <td className="num">{statementMoney(statement.openingBalance)}</td>
                      </tr>
                    )}
                    {statement.lines.length === 0 && (
                      <tr><td colSpan={7} className="statement-empty">No completed services in this period.</td></tr>
                    )}
                    {statement.lines.map((line) => (
                      <tr
                        key={line.noRouteRecord ? `service-${line.serviceId}` : `stop-${line.stopId}`}
                        className={line.unpriced ? 'statement-unpriced-row' : line.pendingDepth ? 'statement-pending-depth-row' : ''}
                      >
                        <td className="statement-date">{line.dateText || 'Date not recorded'}</td>
                        <td>
                          <div className="statement-service">{line.service}</div>
                          {line.noRouteRecord && <div className="statement-note">No route record</div>}
                          {line.address && <div className="statement-note">{line.address}</div>}
                          {line.details.length > 0 && <div className="statement-note">{line.details.join(' · ')}</div>}
                          {line.noCharge && (
                            <div className="statement-note">
                              No charge{line.compReason ? `: ${line.compReason}` : ''}
                            </div>
                          )}
                          {line.discountLabel && <div className="statement-note">Discount: {line.discountLabel}</div>}
                          {line.paymentStatus === 'paid' && (line.paymentDateText || line.paymentMethod) && (
                            <div className="statement-note">
                              Paid{line.paymentDateText ? ` ${line.paymentDateText}` : ''}
                              {line.paymentMethod ? ` by ${line.paymentMethod}` : ''}
                              {line.reconstructed ? ' *' : ''}
                            </div>
                          )}
                        </td>
                        {line.pendingDepth ? (
                          // Completed before the storm depth was entered: no
                          // charge exists yet. An amount taken at the door is
                          // shown as paid (a credit until the depth prices it).
                          <>
                            <td className="num">-</td>
                            <td className="num">-</td>
                            <td className="num statement-awaiting-depth">{AWAITING_DEPTH_LABEL}</td>
                            <td className="num">{line.paid !== null ? formatCurrency(line.paid) : '-'}</td>
                            <td className="num">{statementMoney(line.runningBalance)}</td>
                          </>
                        ) : line.unpriced ? (
                          <>
                            <td className="num">-</td>
                            <td className="num">-</td>
                            <td className="num statement-price-not-set">PRICE NOT SET</td>
                            <td className="num">{line.paid !== null ? formatCurrency(line.paid) : '-'}</td>
                            <td className="num">{statementMoney(line.runningBalance)}</td>
                          </>
                        ) : line.noRouteRecord ? (
                          // Only the amount charged survives a deleted route:
                          // no price or discount is invented for it.
                          <>
                            <td className="num">-</td>
                            <td className="num">-</td>
                            <td className="num">{formatCurrency(line.charged)}</td>
                            <td className="num">{formatCurrency(line.paid)}</td>
                            <td className="num">{statementMoney(line.runningBalance)}</td>
                          </>
                        ) : (
                          <>
                            <td className="num">{formatCurrency(line.price)}</td>
                            <td className="num">{line.discount > 0 ? `-${formatCurrency(line.discount)}` : formatCurrency(0)}</td>
                            <td className="num">{line.noCharge ? 'No charge' : formatCurrency(line.charged)}</td>
                            <td className="num">{formatCurrency(line.paid)}{line.reconstructed ? ' *' : ''}</td>
                            <td className="num">{statementMoney(line.runningBalance)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="statement-totals">
                  <div><span>Services this period</span><span>{formatCurrency(statement.totals.price)}</span></div>
                  <div><span>Discounts</span><span>-{formatCurrency(statement.totals.discount)}</span></div>
                  <div><span>Charged</span><span>{formatCurrency(statement.totals.charged)}</span></div>
                  <div><span>Paid</span><span>-{formatCurrency(statement.totals.paid)}</span></div>
                  {statement.period.start && (
                    <div><span>Balance brought forward</span><span>{statementMoney(statement.openingBalance)}</span></div>
                  )}
                  <div className="statement-due">
                    <span>{statement.closingBalance < 0 ? 'Credit' : 'Balance due'}</span>
                    <span>{formatCurrency(Math.abs(statement.closingBalance))}</span>
                  </div>
                </div>

                {/* The balance due is what was owed on the end date. Stops
                    completed since are not listed, so the full figure is
                    printed here and the reader never takes the smaller one
                    for everything owed. */}
                {statement.after && statement.after.count > 0 && (
                  <p className="statement-footnote statement-after">
                    {statement.after.count === 1
                      ? `1 service completed after ${statement.period.endText} is not shown.`
                      : `${statement.after.count} services completed after ${statement.period.endText} are not shown.`}
                    {' '}Owed in total: {statementMoney(statement.after.totalOwed)}.
                    {statement.after.unpricedCount > 0 && (
                      ` ${statement.after.unpricedCount} of them ${statement.after.unpricedCount === 1 ? 'has' : 'have'} no price set and ${statement.after.unpricedCount === 1 ? 'is' : 'are'} not included.`
                    )}
                  </p>
                )}

                {statement.unpricedCount > 0 && (
                  <p className="statement-footnote">
                    {statement.unpricedCount === 1
                      ? '1 service in this period has no price set and is not included in these totals.'
                      : `${statement.unpricedCount} services in this period have no price set and are not included in these totals.`}
                  </p>
                )}
                {statement.noRouteRecordCount > 0 && (
                  <p className="statement-footnote">
                    {statement.noRouteRecordCount === 1
                      ? '1 service has no route record. Its route was deleted after completion; the date, amount and payment status shown are what was recorded at the time.'
                      : `${statement.noRouteRecordCount} services have no route record. Their route was deleted after completion; the date, amount and payment status shown are what was recorded at the time.`}
                  </p>
                )}
                {statement.reconstructedCount > 0 && (
                  <p className="statement-footnote">
                    * Amount reconstructed from the price at the time, not recorded at payment.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

            {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)} aria-label="Close">&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              {error && (
                <div className="alert alert-error" aria-live="polite">
                  {error}
                  {errorIsLimit && (
                    <>
                      {' '}
                      <a href="/pricing" target="_blank" rel="noopener noreferrer">
                        See plans
                      </a>
                      , or archive a customer you no longer serve to free a slot.
                    </>
                  )}
                </div>
              )}

              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      className="form-input"
                      value={formData.firstName}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      className="form-input"
                      value={formData.lastName}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Address *</label>
                  <input
                    type="text"
                    name="address"
                    className="form-input"
                    value={formData.address}
                    onChange={handleFormChange}
                    required
                  />
                </div>

                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">City *</label>
                    <input
                      type="text"
                      name="city"
                      className="form-input"
                      value={formData.city}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State *</label>
                    <input
                      type="text"
                      name="state"
                      className="form-input"
                      value={formData.state}
                      onChange={handleFormChange}
                      maxLength={2}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">ZIP *</label>
                    <input
                      type="text"
                      name="zip"
                      className="form-input"
                      value={formData.zip}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      className="form-input"
                      value={formData.phone}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="form-input"
                      value={formData.email}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-divider">Service Details</div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Tier</label>
                    <select
                      name="snowTierId"
                      className="form-select"
                      value={formData.snowTierId}
                      onChange={handleFormChange}
                    >
                      {pricingTiers.map(tier => (
                        <option key={tier.id} value={String(tier.id)}>
                          {tier.name} - ${parseFloat(tier.default_price).toFixed(2)}
                        </option>
                      ))}
                      <option value="custom">Custom</option>
                    </select>
                    <RequestedSizeNote requestedName={formData.requestedSnowTierName} />
                    <TierSizeDefinition tierId={formData.snowTierId} tiers={pricingTiers} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Method</label>
                    <select
                      name="paymentMethodPreferred"
                      className="form-select"
                      value={formData.paymentMethodPreferred}
                      onChange={handleFormChange}
                    >
                      {methodOptionsFor(formData.paymentMethodPreferred).map(method => (
                        <option key={method.id} value={method.id}>{method.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">Base Price *</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        type="number"
                        name="priceSnow"
                        className="form-input"
                        value={formData.priceSnow}
                        onChange={handleFormChange}
                        step="0.01"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Salt Price</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        type="number"
                        name="saltPrice"
                        className="form-input"
                        value={formData.saltPrice}
                        onChange={handleFormChange}
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Total</label>
                    <div className="total-price-display">
                      ${(parseFloat(formData.priceSnow || 0) + (formData.saltPreference === 'always' ? parseFloat(formData.saltPrice || 0) : 0)).toFixed(2)}
                      {formData.saltPreference === 'always' && <span className="salt-indicator">+ Salt</span>}
                      {formData.saltPreference === 'as_needed' && <span className="salt-indicator">+ Salt (as needed)</span>}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Service Preference</label>
                    <select
                      name="servicePreference"
                      className="form-select"
                      value={formData.servicePreference}
                      onChange={handleFormChange}
                    >
                      <option value="seasonal">Seasonal (contracted for the season)</option>
                      <option value="on_call">On-Call / As Needed</option>
                    </select>
                    {onCallPremium && formData.servicePreference !== 'seasonal' && (
                      <span className="frequency-badge as-needed on-call-premium form-premium-badge">{onCallLabel}</span>
                    )}
                    {onCallPremium && (
                      <small className="form-hint">On-call snow, salt and add-ons are billed {Number(onCallPremium.toFixed(2))}% above seasonal rates on depth-priced routes.</small>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Service when snowfall reaches...</label>
                    <select
                      name="snowfallThreshold"
                      className="form-select"
                      value={formData.snowfallThreshold}
                      onChange={handleFormChange}
                    >
                      <option value="any">Any amount</option>
                      <option value="1">1 inch+</option>
                      <option value="2">2 inches+</option>
                      <option value="3">3 inches+</option>
                      <option value="4">4 inches+</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Service Type</label>
                    <select
                      name="serviceType"
                      className="form-select"
                      value={formData.serviceType}
                      onChange={handleFormChange}
                    >
                      <option value="snow_removal">Snow Removal</option>
                      <option value="lawn_care">Lawn Care</option>
                      <option value="both">Both Services</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">How did you find me?</label>
                  <select
                    name="leadSource"
                    className="form-select"
                    value={formData.leadSource}
                    onChange={handleFormChange}
                  >
                    <option value="">-- Select --</option>
                    {enabledLeadSources.map(source => (
                      <option key={source.id} value={source.id}>{source.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Referred By</label>
                  <select
                    name="referredById"
                    className="form-select"
                    value={formData.referredById}
                    onChange={handleFormChange}
                  >
                    <option value="">None</option>
                    {customers
                      .filter(c => !editingCustomer || c.id !== editingCustomer.id)
                      .sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`))
                      .map(c => (
                        <option key={c.id} value={c.id}>
                          {c.last_name}, {c.first_name}
                        </option>
                      ))
                    }
                  </select>
                  <small className="text-muted">Customer who referred this customer (for referral discounts)</small>
                </div>

                {editingCustomer && (() => {
                  const referrer = editingCustomer.referred_by_id
                    ? customers.find(c => c.id === editingCustomer.referred_by_id)
                    : null;
                  const referrals = customers.filter(c => c.referred_by_id === editingCustomer.id);

                  if (!referrer && referrals.length === 0) return null;

                  return (
                    <div className="form-group referrals-section">
                      {referrer && (
                        <div className="referred-by-display">
                          <label className="form-label">Referred by:</label>
                          <div className="referral-item">
                            <span className="referral-name">{referrer.first_name} {referrer.last_name}</span>
                            <span className={`referral-status ${referrer.is_active ? 'active' : 'inactive'}`}>
                              {referrer.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      )}
                      {referrals.length > 0 && (
                        <>
                          <label className="form-label" style={referrer ? {marginTop: '12px'} : {}}>
                            Referrals ({referrals.length}):
                          </label>
                          <div className="referrals-list">
                            {referrals.map(r => (
                              <div key={r.id} className="referral-item">
                                <span className="referral-name">{r.first_name} {r.last_name}</span>
                                <span className={`referral-status ${r.is_active ? 'active' : 'inactive'}`}>
                                  {r.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}

                <div className="form-group">
                  <label className="form-label">Services Included</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="driveway"
                        checked={formData.driveway}
                        onChange={handleFormChange}
                      />
                      Driveway
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="sidewalks"
                        checked={formData.sidewalks}
                        onChange={handleFormChange}
                      />
                      Sidewalks
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="walkways"
                        checked={formData.walkways}
                        onChange={handleFormChange}
                      />
                      Walkways
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Salt Preference</label>
                  <select
                    name="saltPreference"
                    className="form-input"
                    value={formData.saltPreference}
                    onChange={handleFormChange}
                  >
                    <option value="never">No Salt</option>
                    <option value="always">Always Salt</option>
                    <option value="as_needed">Salt As Needed</option>
                  </select>
                </div>

                <div className="form-divider">Billing Address</div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="billingSameAsService"
                      checked={formData.billingSameAsService}
                      onChange={handleFormChange}
                    />
                    Billing address same as service address
                  </label>
                </div>

                {!formData.billingSameAsService && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Billing Name</label>
                      <input
                        type="text"
                        name="billingName"
                        className="form-input"
                        value={formData.billingName}
                        onChange={handleFormChange}
                        placeholder="Name for invoice"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Billing Address</label>
                      <input
                        type="text"
                        name="billingAddress"
                        className="form-input"
                        value={formData.billingAddress}
                        onChange={handleFormChange}
                      />
                    </div>

                    <div className="form-row form-row-3">
                      <div className="form-group">
                        <label className="form-label">City</label>
                        <input
                          type="text"
                          name="billingCity"
                          className="form-input"
                          value={formData.billingCity}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">State</label>
                        <input
                          type="text"
                          name="billingState"
                          className="form-input"
                          value={formData.billingState}
                          onChange={handleFormChange}
                          maxLength={2}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">ZIP</label>
                        <input
                          type="text"
                          name="billingZip"
                          className="form-input"
                          value={formData.billingZip}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Discount Section */}
                <div className="form-section-title">Discount</div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Discount Type</label>
                    <select
                      name="discountType"
                      className="form-select"
                      value={formData.discountType}
                      onChange={handleFormChange}
                    >
                      <option value="">None</option>
                      <option value="dollar">$ (Dollar)</option>
                      <option value="percent">% (Percent)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Amount</label>
                    <input
                      type="number"
                      name="discountAmount"
                      className="form-input"
                      value={formData.discountAmount}
                      onChange={handleFormChange}
                      placeholder={formData.discountType === 'percent' ? '10' : '5.00'}
                      step={formData.discountType === 'percent' ? '1' : '0.01'}
                      min="0"
                      disabled={!formData.discountType}
                    />
                  </div>
                </div>
                {formData.discountType && (
                  <div className="form-group">
                    <label className="form-label">Reason</label>
                    <input
                      type="text"
                      name="discountReason"
                      className="form-input"
                      value={formData.discountReason}
                      onChange={handleFormChange}
                      placeholder="e.g., Senior discount, Referral bonus"
                    />
                  </div>
                )}
                {formData.discountType && formData.discountAmount && (
                  <div className="discount-preview">
                    <span className="preview-label">Price Preview:</span>
                    <span className="original-price">${parseFloat(formData.priceSnow || 0).toFixed(2)}</span>
                    <span className="arrow">→</span>
                    <span className="discounted-price">
                      ${calculateDiscountedPrice(
                        parseFloat(formData.priceSnow || 0),
                        parseFloat(formData.discountAmount || 0),
                        formData.discountType
                      ).toFixed(2)}
                    </span>
                    <span className="discount-info">
                      ({formData.discountType === 'percent' ? `${formData.discountAmount}% off` : `$${formData.discountAmount} off`})
                    </span>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    name="notes"
                    className="form-textarea"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={3}
                    placeholder="Special instructions, gate codes, etc."
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="emailSubscribed"
                      checked={formData.emailSubscribed}
                      onChange={handleFormChange}
                    />
                    Subscribed to emails
                  </label>
                  <span className="text-muted" style={{ fontSize: '12px' }}>Customer will receive bulk emails (storm alerts, service updates, seasonal announcements)</span>
                  {editingCustomer && !formData.emailSubscribed && selfUnsubscribedNote(editingCustomer, timezone) && (
                    <span className="text-muted" style={{ display: 'block', fontSize: '12px', color: 'var(--color-warning-text, #8a6d3b)' }} data-testid="self-unsubscribed-note">
                      {selfUnsubscribedNote(editingCustomer, timezone)}
                    </span>
                  )}
                </div>

                {/* Do Not Serve: edit only, since a new customer is never
                    flagged. Set here on the customer; every property inherits
                    it. The reason is private to owner and admin and never
                    reaches the customer. */}
                {editingCustomer && (
                  <div className="form-group">
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="doNotServe"
                        checked={formData.doNotServe}
                        onChange={handleFormChange}
                      />
                      Do Not Serve
                    </label>
                    <span className="text-muted" style={{ fontSize: '12px' }}>
                      Adding this customer or any of their properties to a route will ask for confirmation first. Nothing else changes, and the customer is not told.
                    </span>
                    {formData.doNotServe && (
                      <textarea
                        name="doNotServeReason"
                        className="form-textarea"
                        value={formData.doNotServeReason}
                        onChange={handleFormChange}
                        rows={2}
                        maxLength={500}
                        placeholder="Reason (private to owner and admin)"
                        style={{ marginTop: '8px' }}
                      />
                    )}
                  </div>
                )}

                {/* Text messages: edit only, owner and admin. Saved on its own
                    request, apart from Save Changes; see SmsConsentField. */}
                {editingCustomer && isAdmin() && (
                  <SmsConsentField customerId={editingCustomer.id} onChange={() => loadCustomers()} />
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingCustomer ? 'Save Changes' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Property Modal */}
      {showPropertyModal && (
        <div className="modal-overlay" onClick={() => setShowPropertyModal(false)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>{editingProperty ? 'Edit Property' : 'Add Property'}</h2>
              <button className="modal-close" onClick={() => setShowPropertyModal(false)} aria-label="Close">&times;</button>
            </div>

            <form onSubmit={handlePropertySubmit}>
              {error && <div className="alert alert-error" aria-live="polite">{error}</div>}

              <div className="modal-body">
                {propertyParent && (
                  <div className="property-parent-info">
                    <span className="parent-label">Customer:</span>
                    <span className="parent-name">{propertyParent.first_name} {propertyParent.last_name}</span>
                  </div>
                )}

                {/* Read-only here: a property inherits Do Not Serve from its
                    customer, and it is changed on the customer. */}
                {propertyParent?.do_not_serve && (
                  <div className="form-group">
                    <DoNotServeBadge reason={propertyParent.do_not_serve_reason} inherited style={{ marginLeft: 0 }} />
                    <span className="text-muted" style={{ fontSize: '12px', marginLeft: '8px' }}>
                      Inherited from {propertyParent.first_name} {propertyParent.last_name}
                    </span>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Address *</label>
                  <input
                    type="text"
                    name="address"
                    className="form-input"
                    value={propertyFormData.address}
                    onChange={handlePropertyFormChange}
                    required
                  />
                </div>

                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">City *</label>
                    <input
                      type="text"
                      name="city"
                      className="form-input"
                      value={propertyFormData.city}
                      onChange={handlePropertyFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State *</label>
                    <input
                      type="text"
                      name="state"
                      className="form-input"
                      value={propertyFormData.state}
                      onChange={handlePropertyFormChange}
                      maxLength={2}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">ZIP *</label>
                    <input
                      type="text"
                      name="zip"
                      className="form-input"
                      value={propertyFormData.zip}
                      onChange={handlePropertyFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-divider">Pricing & Services</div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Property Tier</label>
                    <select
                      name="snowTierId"
                      className="form-select"
                      value={propertyFormData.snowTierId}
                      onChange={handlePropertyFormChange}
                    >
                      {pricingTiers.map(tier => (
                        <option key={tier.id} value={String(tier.id)}>
                          {tier.name} - ${parseFloat(tier.default_price).toFixed(2)}
                        </option>
                      ))}
                      <option value="custom">Custom</option>
                    </select>
                    <RequestedSizeNote requestedName={propertyFormData.requestedSnowTierName} />
                    <TierSizeDefinition tierId={propertyFormData.snowTierId} tiers={pricingTiers} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Method</label>
                    <select
                      name="paymentMethodPreferred"
                      className="form-select"
                      value={propertyFormData.paymentMethodPreferred}
                      onChange={handlePropertyFormChange}
                    >
                      {methodOptionsFor(propertyFormData.paymentMethodPreferred).map(method => (
                        <option key={method.id} value={method.id}>{method.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Base Price *</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        type="number"
                        name="priceSnow"
                        className="form-input"
                        value={propertyFormData.priceSnow}
                        onChange={handlePropertyFormChange}
                        step="0.01"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Salt Price</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        type="number"
                        name="saltPrice"
                        className="form-input"
                        value={propertyFormData.saltPrice}
                        onChange={handlePropertyFormChange}
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Services Included</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="driveway"
                        checked={propertyFormData.driveway}
                        onChange={handlePropertyFormChange}
                      />
                      Driveway
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="sidewalks"
                        checked={propertyFormData.sidewalks}
                        onChange={handlePropertyFormChange}
                      />
                      Sidewalks
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="walkways"
                        checked={propertyFormData.walkways}
                        onChange={handlePropertyFormChange}
                      />
                      Walkways
                    </label>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Salt Preference</label>
                    <select
                      name="saltPreference"
                      className="form-input"
                      value={propertyFormData.saltPreference}
                      onChange={handlePropertyFormChange}
                    >
                      <option value="never">No Salt</option>
                      <option value="always">Always Salt</option>
                      <option value="as_needed">Salt As Needed</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Service Type</label>
                    <select
                      name="serviceType"
                      className="form-input"
                      value={propertyFormData.serviceType}
                      onChange={handlePropertyFormChange}
                    >
                      <option value="snow_removal">Snow Removal</option>
                      <option value="lawn_care">Lawn Care</option>
                      <option value="both">Both Services</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    name="notes"
                    className="form-textarea"
                    value={propertyFormData.notes}
                    onChange={handlePropertyFormChange}
                    rows={2}
                    placeholder="Special instructions for this property"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowPropertyModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingProperty ? 'Save Changes' : 'Add Property'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .tier-size-definition {
          margin-top: 6px;
        }
        .tier-size-framing,
        .tier-size-meaning {
          margin: 0;
          font-size: 0.8125rem;
          line-height: 1.4;
          color: var(--color-text-muted, #6b7280);
        }
        .tier-size-meaning {
          margin-top: 4px;
          font-weight: 500;
          color: var(--color-text, #374151);
        }
        .requested-size-note {
          margin: 6px 0 0 0;
          font-size: 0.8125rem;
          font-weight: 600;
          color: var(--color-primary, #2563eb);
        }
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }

        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 16px;
        }

        .filters-bar {
          display: flex;
          gap: 16px;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .search-input {
          flex: 1;
          min-width: 200px;
          max-width: 400px;
        }
        /* Checkbox styles in index.css */

        .empty-state {
          text-align: center;
          padding: 48px 16px;
          color: var(--color-text-light);
        }
        .empty-state .btn {
          margin-top: 16px;
        }

        .customer-list {
          display: grid;
          gap: 16px;
        }

        .customer-card {
          background: var(--color-card);
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .customer-card.inactive {
          opacity: 0.6;
          background: #f9f9f9;
        }

        .customer-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }
        .customer-name {
          font-size: 1.125rem;
          font-weight: 600;
          margin: 0 0 4px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .inactive-badge {
          font-size: 0.75rem;
          background: #999;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .intake-badge {
          font-size: 0.7rem;
          background: #f59e0b;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        /* The customer stated a driveway size and the owner has not agreed with
           it yet. Same weight as .intake-badge, different hue, because it is a
           different kind of "look at this": the row exists and is fine, one
           decision on it is outstanding. */
        .size-pending-badge {
          font-size: 0.7rem;
          background: #2563eb;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
          white-space: nowrap;
          cursor: help;
        }
        .welcome-badge {
          font-size: 0.7rem;
          background: #ecfdf5;
          color: #047857;
          border: 1px solid #a7f3d0;
          padding: 1px 8px;
          border-radius: 4px;
          font-weight: 500;
          white-space: nowrap;
          cursor: help;
        }

        .season-badge {
          font-size: 0.7rem;
          padding: 1px 8px;
          border-radius: 4px;
          font-weight: 500;
          white-space: nowrap;
          cursor: help;
          border: 1px solid transparent;
        }
        .season-badge.season-confirmed { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
        .season-badge.season-declined { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
        .season-badge.season-pending {
          background: transparent;
          color: var(--color-text-light);
          border-color: var(--color-border);
          font-weight: 400;
        }
        .season-answer-btn {
          font-size: 0.7rem;
          padding: 1px 8px;
        }
        .season-filter-select {
          width: auto;
          padding: 4px 8px;
          margin-left: 6px;
        }
        .season-chooser {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
          margin: 8px 0;
          padding: 8px;
          border: 1px solid var(--color-border);
          border-radius: 6px;
          background: var(--color-bg, #f9fafb);
        }
        .season-chooser .form-select {
          width: auto;
          padding: 4px 8px;
          font-size: 0.85rem;
        }
        .season-chooser-title {
          font-size: 0.85rem;
          font-weight: 500;
        }
        .size-confirm-btn {
          font-size: 0.7rem;
          background: white;
          color: #2563eb;
          border: 1px solid #2563eb;
          padding: 1px 8px;
          border-radius: 4px;
          font-weight: 600;
          white-space: nowrap;
          cursor: pointer;
        }
        .size-confirm-btn:hover:not(:disabled) {
          background: #eff6ff;
        }
        .size-confirm-btn:disabled {
          opacity: 0.5;
          cursor: default;
        }
        /* "No price set" where a currency figure would otherwise sit. Muted and
           italic so it does not read as an amount, and normal-weight so it does
           not compete with the real prices in the same column. */
        .price-unset {
          color: #6b7280;
          font-style: italic;
          font-weight: 400;
        }
        /* Held by the plan customer limit. Deliberately louder than
           .inactive-badge: this one needs an action from the owner. */
        .held-badge {
          font-size: 0.7rem;
          background: #92400e;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 600;
          white-space: nowrap;
        }
        .held-banner {
          background: #fef3c7;
          border-left: 4px solid #d97706;
          color: #78350f;
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 16px;
          font-size: 0.9rem;
          line-height: 1.55;
        }
        .portal-badge {
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .portal-badge.active {
          background: #dcfce7;
          color: #15803d;
        }
        .portal-badge.invited {
          background: #dbeafe;
          color: #1d4ed8;
        }
        .btn-portal {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
          color: white !important;
          border: none !important;
        }
        .btn-portal:hover:not(:disabled) {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
        }
        .btn-portal:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .customer-address, .customer-city {
          margin: 0;
          color: var(--color-text-light);
          font-size: 0.875rem;
        }
        .customer-price {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-primary);
        }
        .customer-price .price-original {
          text-decoration: line-through;
          color: #9ca3af;
          font-size: 1rem;
          margin-right: 6px;
        }
        .customer-price .price-discounted {
          color: #059669;
        }
        .discount-info-row {
          font-size: 0.8rem;
          color: #059669;
          padding: 4px 12px;
          background: rgba(5, 150, 105, 0.08);
          margin: 0 -12px;
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }
        .discount-label {
          font-weight: 600;
        }
        .discount-reason {
          color: #6b7280;
          font-style: italic;
        }

        .customer-card-body {
          margin-bottom: 12px;
        }
        .customer-phone {
          display: inline-block;
          color: var(--color-primary);
          margin-bottom: 8px;
        }
        .customer-email {
          display: block;
          color: var(--color-primary);
          margin-bottom: 8px;
          overflow-wrap: anywhere;
        }
        .customer-email-missing {
          margin: 0 0 8px 0;
          font-size: 0.85rem;
          color: var(--color-text-light);
        }
        .referred-by-text {
          margin: 0 0 8px 0;
          font-size: 0.85rem;
          color: #059669;
          font-style: italic;
        }
        .service-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .service-badge {
          font-size: 0.75rem;
          background: var(--color-primary-light);
          color: var(--color-primary-dark);
          padding: 4px 8px;
          border-radius: 4px;
        }
        .addon-badge {
          font-size: 0.75rem;
          background: #ecfdf5;
          color: #065f46;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 500;
          overflow-wrap: anywhere;
        }
        .referral-badge {
          font-size: 0.75rem;
          background: #10b981;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .lead-source-badge {
          font-size: 0.75rem;
          background: #8b5cf6;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .frequency-badge {
          font-size: 0.75rem;
          padding: 4px 10px;
          border-radius: 12px;
          font-weight: 500;
        }
        .frequency-badge.seasonal {
          background: #dbeafe;
          color: #1e40af;
        }
        .frequency-badge.as-needed {
          background: #fef3c7;
          color: #92400e;
        }
        .frequency-badge.on-call-premium {
          font-weight: 600;
          white-space: nowrap;
        }
        .form-premium-badge {
          display: inline-block;
          margin-top: 6px;
        }
        .form-hint {
          display: block;
          margin-top: 4px;
          font-size: 0.8rem;
          color: var(--color-text-light);
        }
        .threshold-badge {
          font-size: 0.75rem;
          background: #e5e7eb;
          color: #374151;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .service-pref-badge {
          font-size: 0.75rem;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .service-pref-badge.seasonal {
          background: #dcfce7;
          color: #166534;
        }

        .customer-card-footer {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: space-between;
          align-items: center;
          padding-top: 12px;
          border-top: 1px solid var(--color-border);
        }
        .payment-method {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        /* Up to six buttons; they wrap rather than widen the card at phone width. */
        .customer-actions {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 8px;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }
        .modal {
          background: white;
          border-radius: 12px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid var(--color-border);
        }
        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--color-text-light);
          line-height: 1;
        }
        .modal-body {
          padding: 20px;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid var(--color-border);
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .form-row-3 {
          grid-template-columns: 2fr 1fr 1fr;
        }
        .form-section-title {
          font-weight: 600;
          color: var(--color-text);
          margin: 20px 0 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--color-border);
          font-size: 0.95rem;
        }
        .discount-preview {
          background: rgba(5, 150, 105, 0.08);
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .discount-preview .preview-label {
          font-weight: 500;
          color: #374151;
        }
        .discount-preview .original-price {
          text-decoration: line-through;
          color: #9ca3af;
        }
        .discount-preview .arrow {
          color: #9ca3af;
        }
        .discount-preview .discounted-price {
          font-weight: 700;
          color: #059669;
          font-size: 1.1rem;
        }
        .discount-preview .discount-info {
          font-size: 0.85rem;
          color: #6b7280;
        }
        .form-divider {
          font-weight: 600;
          color: var(--color-text);
          margin: 20px 0 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--color-border);
        }
        .input-with-prefix {
          display: flex;
          align-items: center;
        }
        .input-prefix {
          background: var(--color-background);
          padding: 0 12px;
          height: 44px;
          display: flex;
          align-items: center;
          border: 1px solid var(--color-border);
          border-right: none;
          border-radius: 8px 0 0 8px;
          color: var(--color-text-light);
        }
        .input-with-prefix .form-input {
          border-radius: 0 8px 8px 0;
        }
        /* checkbox-group styles in index.css */
        .form-textarea {
          resize: vertical;
          min-height: 80px;
        }

        .total-price-display {
          display: flex;
          align-items: center;
          gap: 8px;
          height: 44px;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--color-primary);
        }
        .salt-indicator {
          font-size: 0.75rem;
          font-weight: 500;
          color: #059669;
          background: #d1fae5;
          padding: 2px 6px;
          border-radius: 4px;
        }
        .salt-badge-small {
          display: inline-block;
          font-size: 0.65rem;
          font-weight: 600;
          background: #d1fae5;
          color: #059669;
          padding: 2px 6px;
          border-radius: 4px;
          margin-left: 6px;
          vertical-align: middle;
        }
        .tier-badge {
          background: #e0e7ff;
          color: #3730a3;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        /* Custom means "no tier", so it is muted rather than coloured. It is
           still shown, because an absent badge used to be the only way Custom
           was indicated and that was indistinguishable from a rendering bug. */
        .tier-badge.custom {
          background: #f1f5f9;
          color: #64748b;
        }

        /* The editable form of the badge above. Deliberately the same colours,
           the same radius and the same font size, so turning the chip into a
           control does not turn it into a form field: it is still a badge, it
           just opens. Only the caret and the 10px of room for it are new. */
        .tier-select {
          background-color: #e0e7ff;
          color: #3730a3;
          padding: 2px 18px 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
          border: 1px solid transparent;
          cursor: pointer;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='3'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 5px center;
        }

        .tier-select.custom {
          background-color: #f1f5f9;
          color: #64748b;
        }

        .tier-select:hover {
          border-color: var(--color-border);
        }

        .tier-select:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        .tier-select:disabled {
          opacity: 0.6;
          cursor: wait;
        }

        /* Mobile Menu */
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }

        .referrals-section {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          padding: 12px;
          margin-top: 8px;
        }
        .referrals-section .form-label {
          color: #166534;
          margin-bottom: 8px;
        }
        .referrals-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .referral-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: white;
          padding: 8px 12px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }
        .referral-name {
          font-weight: 500;
          color: #374151;
        }
        .referral-status {
          font-size: 0.75rem;
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 500;
        }
        .referral-status.active {
          background: #dcfce7;
          color: #166534;
        }
        .referral-status.inactive {
          background: #f3f4f6;
          color: #6b7280;
        }

        /* Property badge */
        .property-count-badge {
          font-size: 0.7rem;
          background: #7c3aed;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }

        /* Property button */
        .btn-property {
          background: #7c3aed;
          color: white;
          border: none;
        }
        .btn-property:hover {
          background: #6d28d9;
        }

        /* Properties toggle button */
        .properties-toggle {
          display: block;
          width: 100%;
          background: #f5f3ff;
          border: 1px solid #ddd6fe;
          border-radius: 6px;
          padding: 10px 16px;
          margin-top: 12px;
          cursor: pointer;
          text-align: left;
          font-size: 0.875rem;
          font-weight: 500;
          color: #7c3aed;
          transition: all 0.15s ease;
        }
        .properties-toggle:hover {
          background: #ede9fe;
        }

        /* Properties list */
        .properties-list {
          margin-top: 12px;
          padding-left: 16px;
          border-left: 3px solid #7c3aed;
        }

        .property-card {
          display: flex;
          align-items: center;
          gap: 16px;
          background: #faf5ff;
          border: 1px solid #e9d5ff;
          border-radius: 8px;
          padding: 12px 16px;
          margin-bottom: 8px;
        }
        .property-card:last-child {
          margin-bottom: 0;
        }

        /* The card the Dashboard size banner sent the owner to. Amber, the
           banner's own colour, so the two read as one thread. */
        .customer-card.focus-highlight,
        .property-card.focus-highlight {
          animation: focus-highlight-pulse 2.5s ease-out;
        }
        @keyframes focus-highlight-pulse {
          0%, 40% {
            box-shadow: 0 0 0 3px #f59e0b;
            background-color: #fffbeb;
          }
          100% {
            box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
          }
        }

        .property-info {
          flex: 1;
        }
        .property-address {
          margin: 0;
          font-weight: 500;
          font-size: 0.9rem;
          color: #374151;
        }
        .property-city {
          margin: 0;
          font-size: 0.8rem;
          color: #6b7280;
        }
        .property-services {
          display: flex;
          gap: 4px;
          margin-top: 6px;
          flex-wrap: wrap;
        }
        .service-badge-sm {
          font-size: 0.65rem;
          background: #e0e7ff;
          color: #3730a3;
          padding: 2px 6px;
          border-radius: 3px;
        }
        .service-badge-sm.salt {
          background: #d1fae5;
          color: #059669;
        }

        .tier-badge-sm {
          font-size: 0.65rem;
          background: #e0e7ff;
          color: #3730a3;
          padding: 2px 6px;
          border-radius: 3px;
          font-weight: 500;
        }

        .tier-badge-sm.custom {
          background: #f1f5f9;
          color: #64748b;
        }

        /* Property-row size of the same control, matching .tier-badge-sm. */
        .tier-select-sm {
          font-size: 0.65rem;
          background-color: #e0e7ff;
          color: #3730a3;
          padding: 2px 16px 2px 6px;
          border-radius: 3px;
          font-weight: 500;
          border: 1px solid transparent;
          cursor: pointer;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='3'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 4px center;
        }

        .tier-select-sm.custom {
          background-color: #f1f5f9;
          color: #64748b;
        }

        .tier-select-sm:hover {
          border-color: var(--color-border);
        }

        .tier-select-sm:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        .tier-select-sm:disabled {
          opacity: 0.6;
          cursor: wait;
        }

        .property-pricing {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
        }

        .property-price {
          font-size: 1.1rem;
          font-weight: 600;
          color: #7c3aed;
        }

        .property-payment {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .property-actions {
          display: flex;
          gap: 6px;
        }

        .btn-xs {
          font-size: 0.7rem;
          padding: 4px 8px;
        }

        /* Property modal */
        .modal-sm {
          max-width: 480px;
        }
        .property-parent-info {
          background: #f5f3ff;
          border: 1px solid #ddd6fe;
          border-radius: 6px;
          padding: 10px 12px;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .parent-label {
          font-size: 0.8rem;
          color: #6b7280;
        }
        .parent-name {
          font-weight: 600;
          color: #7c3aed;
        }

        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .form-row, .form-row-3 {
            grid-template-columns: 1fr;
          }
          .customer-card-header {
            flex-direction: column;
            gap: 8px;
          }
          .customer-price {
            font-size: 1.25rem;
          }
        }
      
        /* Print View Styles */
        .header-buttons {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .btn-secondary-outline {
          background: transparent;
          border: 1px solid #d1d5db;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn-secondary-outline:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        .btn-icon {
          width: 18px;
          height: 18px;
        }

        .print-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          z-index: 1000;
          overflow-y: auto;
        }

        .print-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
        }

        .print-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          border-bottom: 2px solid #e5e7eb;
          margin-bottom: 24px;
        }

        .print-header h2 {
          margin: 0;
          font-size: 1.25rem;
          color: #1f2937;
        }

        .print-actions {
          display: flex;
          gap: 12px;
        }

        .print-page-header {
          text-align: center;
          padding-bottom: 20px;
          border-bottom: 2px solid #1f2937;
          margin-bottom: 24px;
        }

        .print-page-header h1 {
          margin: 0 0 8px 0;
          font-size: 1.75rem;
          color: #1f2937;
        }

        .print-date {
          margin: 0;
          color: #6b7280;
          font-size: 0.9rem;
        }

        .print-count {
          margin: 4px 0 0 0;
          color: #374151;
          font-weight: 500;
        }

        .print-customer-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .print-customer-card {
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 16px;
          page-break-inside: avoid;
        }

        .print-customer-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 12px;
        }

        .print-customer-number {
          background: #1f2937;
          color: white;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.85rem;
          font-weight: 600;
          flex-shrink: 0;
        }

        .print-customer-name {
          margin: 0;
          flex: 1;
          font-size: 1.1rem;
          color: #1f2937;
        }

        .print-customer-price {
          font-size: 1.1rem;
          font-weight: 600;
          color: #059669;
        }

        .print-customer-details {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .print-detail-row {
          display: flex;
          gap: 8px;
          font-size: 0.9rem;
        }

        .print-label {
          font-weight: 500;
          color: #6b7280;
          min-width: 70px;
        }

        .print-value {
          color: #1f2937;
        }

        .print-filter {
          margin: 4px 0 0 0;
          color: #374151;
          font-size: 0.9rem;
        }

        /* Black text in a solid border, so it survives a mono printer that
           drops colour and background. */
        .print-dns {
          display: inline-block;
          margin-left: 10px;
          padding: 1px 6px;
          border: 2px solid #000;
          color: #000;
          font-weight: 700;
          font-size: 0.8rem;
          letter-spacing: 0.04em;
          vertical-align: middle;
        }

        .print-notes {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed #e5e7eb;
        }

        .print-notes .print-value {
          font-style: italic;
          color: #4b5563;
        }

        .print-properties {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
        }

        .print-properties-label {
          font-size: 0.85rem;
          font-weight: 500;
          color: #6b7280;
          display: block;
          margin-bottom: 8px;
        }

        .print-property {
          padding: 4px 0 6px 16px;
          font-size: 0.85rem;
          page-break-inside: avoid;
        }

        .print-property-address {
          color: #1f2937;
          font-weight: 500;
        }

        .print-property-details {
          color: #374151;
          padding-left: 16px;
        }

        .print-notes-inline {
          font-style: italic;
        }

        .print-footer {
          text-align: center;
          padding-top: 24px;
          margin-top: 24px;
          border-top: 1px solid #e5e7eb;
          color: #9ca3af;
          font-size: 0.85rem;
        }

        .balance-owed {
          display: block;
          margin-top: 4px;
          font-size: 0.85rem;
          font-weight: 600;
          color: #b91c1c;
        }

        .balance-unpriced {
          color: #92400e;
          font-weight: 500;
        }

        .statement-container {
          max-width: 960px;
        }

        .statement-actions {
          flex-wrap: wrap;
          align-items: center;
        }

        .statement-period {
          width: auto;
        }

        .statement-warning {
          margin-bottom: 16px;
          padding: 12px 14px;
          border: 1px solid #f59e0b;
          background: #fffbeb;
          color: #92400e;
          border-radius: 6px;
          font-size: 0.9rem;
        }

        .statement {
          color: #111827;
          overflow-x: auto;
        }

        .statement-head {
          display: flex;
          justify-content: space-between;
          gap: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid #111827;
          margin-bottom: 16px;
          font-size: 0.9rem;
        }

        .statement-company-name {
          font-size: 1.25rem;
          font-weight: 700;
        }

        .statement-pre {
          white-space: pre-line;
        }

        /* Black on white in a border, so it survives a mono printer. */
        .statement-missing {
          display: inline-block;
          margin: 2px 0;
          padding: 1px 6px;
          border: 1px dashed #000;
          font-weight: 600;
        }

        .statement-title-block {
          text-align: right;
        }

        .statement-title {
          font-size: 1.5rem;
          font-weight: 700;
          letter-spacing: 0.08em;
        }

        .statement-generated {
          color: #4b5563;
        }

        .statement-billto {
          margin-bottom: 16px;
          font-size: 0.9rem;
        }

        .statement-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #4b5563;
        }

        .statement-billto-name {
          font-weight: 600;
        }

        .statement-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }

        .statement-table th {
          text-align: left;
          border-bottom: 1px solid #111827;
          padding: 6px 6px;
          white-space: nowrap;
        }

        .statement-table td {
          border-bottom: 1px solid #d1d5db;
          padding: 6px 6px;
          vertical-align: top;
        }

        .statement-table .num {
          text-align: right;
          white-space: nowrap;
        }

        .statement-date {
          white-space: nowrap;
        }

        .statement-service {
          font-weight: 500;
        }

        .statement-note {
          color: #4b5563;
          font-size: 0.8rem;
        }

        .statement-price-not-set {
          font-weight: 700;
        }

        .statement-awaiting-depth {
          font-weight: 700;
          color: #92400e;
          white-space: nowrap;
        }

        .statement-opening td {
          font-style: italic;
        }

        .statement-empty {
          text-align: center;
          color: #4b5563;
          padding: 16px;
        }

        .statement-totals {
          margin: 16px 0 0 auto;
          max-width: 340px;
          font-size: 0.9rem;
        }

        .statement-totals > div {
          display: flex;
          justify-content: space-between;
          padding: 3px 0;
        }

        .statement-due {
          border-top: 2px solid #111827;
          margin-top: 4px;
          padding-top: 6px !important;
          font-weight: 700;
          font-size: 1rem;
        }

        .statement-footnote {
          margin: 10px 0 0 0;
          font-size: 0.8rem;
          color: #374151;
        }

        .statement-after {
          text-align: right;
          font-size: 0.9rem;
          font-weight: 600;
          color: #111827;
        }

        /* Print-specific styles */
        @media print {
          .statement {
            overflow: visible;
          }

          .statement-table tr,
          .statement-totals {
            page-break-inside: avoid;
          }

          .statement-table thead {
            display: table-header-group;
          }

          /* display:none, not visibility:hidden: a hidden page keeps its
             layout height and printed as trailing blank pages. The overlay
             is portaled to body, so it is the only body child left. */
          body > *:not(.print-overlay) {
            display: none !important;
          }

          /* index.css gives body min-height: 100vh; plus any margin that
             spills a short list onto a second, empty page. */
          body {
            min-height: 0;
          }

          .print-overlay {
            position: static;
            overflow: visible;
            width: 100%;
          }

          .no-print {
            display: none !important;
          }

          .print-container {
            max-width: 100%;
            padding: 0;
          }

          .print-customer-card {
            border: 1px solid #ccc;
            margin-bottom: 12px;
            page-break-inside: avoid;
          }

          .print-page-header {
            padding-bottom: 16px;
          }

          .print-page-header h1 {
            font-size: 1.5rem;
          }

          .print-customer-number {
            background: #333;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          /* Static, after the last customer: fixed would repeat it on every
             page and could print over the last card on a page. */
          .print-footer {
            position: static;
            page-break-inside: avoid;
          }

          @page {
            margin: 0.5in;
          }
        }
      `}</style>

      {/* Message Customers Modal */}
      <BulkPortalInviteModal
        show={showBulkInvite}
        onClose={() => setShowBulkInvite(false)}
        onFinished={() => Object.keys(portalStatus).forEach((id) => loadPortalStatus(id))}
      />

      <EmailCustomersModal
        show={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        customers={customers}
        routes={[]}
        pricingTiers={pricingTiers}
        canSubscribe={isAdmin()}
        onCustomerSubscribed={(id) => setCustomers((prev) => prev.map((c) => (
          c.id === id ? { ...c, email_subscribed: true, email_unsubscribed_at: null, email_unsubscribe_source: null } : c
        )))}
        smsEnabled={smsEnabled}
        timezone={timezone}
      />

      <MessageHistory
        show={showMessageHistory}
        onClose={() => setShowMessageHistory(false)}
        smsEnabled={smsEnabled}
      />
    </div>
  );
}

export default Customers;
